import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { getUsuarioSessao } from '../utils/sessao';
import pedirSenhaAdmin from '../components/common/pedirSenhaAdmin';
import { gerarCupomVenda, gerarA4Venda, gerarDanfeNfceVenda, gerarPreviewDanfeNfceVenda, gerarPreviewDanfSe, abrirDanfseOficial, abrirDanfeNfePdf, abrirBoleto, fecharComEsc, aguardarImagens } from '../utils/impressaoVenda';
import { hojeBR } from '../utils/data';
import ModalPagamentoVenda from '../components/vendas/ModalPagamentoVenda';
import ModalCompletarCliente from '../components/vendas/ModalCompletarCliente';
import ModalSelecionarCliente from '../components/vendas/ModalSelecionarCliente';
import ModalCorrecaoFiscal from '../components/pdv/ModalCorrecaoFiscal';
import ModalBuscaProdutos from '../components/pdv/ModalBuscaProdutos';
import useBuscaProdutosF3 from '../hooks/useBuscaProdutosF3';
import { calcularPrecoTabela } from '../utils/precoTabela';
import s from './PedidosOrcamentos.module.css';
import InputDataFiltro from '../components/common/InputDataFiltro';
import { imprimirPropostaComercial } from '../utils/propostaComercial';

/* ── Constantes ── */
const ABAS = [
  { id: 'itens',      label: 'Itens'      },
  { id: 'financeiro', label: 'Financeiro' },
  { id: 'transporte', label: 'Transporte' },
  { id: 'obs',        label: 'Observação' },
];
const ABA_PROPOSTA = { id: 'proposta', label: 'Proposta' };

// Campos da aba Proposta (pedidos.proposta). Equipamento é digitado aqui
// mesmo — não há cadastro de equipamentos.
const CAMPOS_EQUIP = [
  ['equip_marca', 'Marca', 110], ['equip_carcaca', 'Carcaça / Modelo', 170], ['equip_serie', 'Nº de Série', 130],
  ['equip_tag', 'Tag', 90], ['equip_tensao', 'Tensão', 90], ['equip_frequencia', 'Frequência', 90],
  ['equip_fabricacao', 'Dt. Fabricação', 100],
];

const STATUS_OPTS = [
  { v: 'aberto',    l: 'Aberto'    },
  { v: 'aprovado',  l: 'Aprovado'  },
  { v: 'cancelado', l: 'Cancelado' },
  { v: 'importado', l: 'Importado' },
];

const TIPO_OPTS = [
  { v: 'pedido',    l: 'Pedido de Venda' },
  { v: 'orcamento', l: 'Orçamento'       },
];

const TIPO_FRETE_OPTS = [
  { v: '0', l: '0 — Por conta do emitente (CIF)'     },
  { v: '1', l: '1 — Por conta do destinatário (FOB)' },
  { v: '2', l: '2 — Por conta de terceiros'           },
  { v: '3', l: '3 — Próprio por conta do remetente'   },
  { v: '9', l: '9 — Sem frete'                        },
];

const FORMA_PGT_OPTS = [
  'Dinheiro', 'Cartão de Crédito', 'Cartão de Débito',
  'PIX', 'Boleto', 'Transferência Bancária',
  'Cheque', 'Crediário', 'A Prazo',
];

const FORM_VAZIO = {
  id: null, venda_id: null, tipo: 'pedido', status: 'aberto',
  data_emissao: hojeBR(), validade: '',
  vendedor: '', vendedor_id: null,
  cliente_id: null, cliente_codigo: '', cliente_nome: '', cliente_cpf_cnpj: '', cliente_tipo_pessoa: 'F',
  tipo_movimento_fiscal_id: '',
  itens: [],
  forma_pagamento: '', condicao_pagamento: '',
  desconto: '', frete: '',
  tipo_frete: '9', transportadora: '', prazo_entrega: '',
  observacoes: '',
  proposta: {},
};

const badgeClass = st => ({
  aberto: s.badgeAberto, aprovado: s.badgeAprovado,
  cancelado: s.badgeCancelado, importado: s.badgeImportado,
}[st] || s.badgeAberto);

const fmtMoeda = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtData  = str => str ? new Date(String(str).slice(0, 10) + 'T12:00:00').toLocaleDateString('pt-BR') : '—';
const sv = v   => (v == null ? '' : String(v));
// Tolerante a maiúsculas/espaços — cadastros antigos ou editados fora da
// tela podem gravar 'SERVIÇO' em vez do 'serviço' canônico do select.
const ehServico = t => String(t || '').trim().toLowerCase() === 'serviço';

function pedidoParaForm(p) {
  return {
    id: p.id,
    tipo: p.tipo || 'pedido',
    status: p.status || 'aberto',
    data_emissao: p.data_emissao ? p.data_emissao.slice(0, 10) : hojeBR(),
    validade: p.validade ? p.validade.slice(0, 10) : '',
    vendedor: p.vendedor || '',
    vendedor_id: p.vendedor_id || null,
    cliente_id: p.cliente_id || null,
    cliente_codigo: sv(p.cliente_id),
    cliente_nome: p.cliente_nome || '',
    cliente_cpf_cnpj: p.cliente_cpf_cnpj || '',
    cliente_tipo_pessoa: p.cl_tipo_pessoa || 'F',
    tipo_movimento_fiscal_id: sv(p.tipo_movimento_fiscal_id),
    itens: (p.itens || []).map((it, i) => {
      // Preço exibido é o efetivo; desconto % é relativo ao preço original (de tabela).
      // Pedidos antigos gravavam preço cheio com o desconto aplicado só no subtotal —
      // detecta pelo subtotal e normaliza para o formato novo.
      const qtdN = parseFloat(it.quantidade) || 0;
      const preN = parseFloat(it.preco_unitario) || 0;
      const desN = parseFloat(it.desconto_perc) || 0;
      const subN = parseFloat(it.subtotal) || 0;
      let precoEfet = preN, precoOrig = preN;
      if (desN && desN < 100) {
        const subAntigo = qtdN * preN * (1 - desN / 100);
        if (Math.abs(subAntigo - subN) < Math.abs(qtdN * preN - subN)) {
          precoEfet = +(preN * (1 - desN / 100)).toFixed(2);
        } else {
          precoOrig = +(preN / (1 - desN / 100)).toFixed(2);
        }
      }
      return {
        _key: it.id || i + 1,
        produto_id: it.produto_id || null,
        codigo_produto: it.codigo_produto || sv(it.produto_codigo_interno) || '',
        descricao: it.descricao || '',
        unidade: it.unidade || 'UN',
        quantidade: sv(it.quantidade),
        preco_unitario: precoEfet.toFixed(2),
        preco_original: precoOrig.toFixed(2),
        desconto_perc: sv(it.desconto_perc || 0),
        subtotal: sv(it.subtotal),
        tipo: it.tipo_item || 'mercadoria',
        setor_id: it.setor_id || null,
        setor_sigla: it.setor_sigla || null,
        setor_nome: it.setor_nome || null,
      };
    }),
    forma_pagamento: p.forma_pagamento || '',
    condicao_pagamento: p.condicao_pagamento || '',
    desconto: p.desconto ? sv(p.desconto) : '',
    frete: p.frete && parseFloat(p.frete) !== 0 ? sv(p.frete) : '',
    tipo_frete: p.tipo_frete || '9',
    transportadora: p.transportadora || '',
    prazo_entrega: p.prazo_entrega ? sv(p.prazo_entrega) : '',
    observacoes: p.observacoes || '',
    proposta: p.proposta || {},
  };
}

// Itens da VENDA (itens_venda) no mesmo formato que o grid/impressão esperam
// (mesmas chaves de pedidoParaForm) — usado pra exibir o pedido "importado"
// com os dados REAIS da venda finalizada, não o rascunho antigo do pedido.
function itensVendaParaForm(itens) {
  return (itens || []).map((it, i) => {
    const orig = parseFloat(it.preco_tabela || 0);
    const efet = parseFloat(it.preco_unitario || 0);
    const descPerc = orig > 0 && efet < orig ? (((orig - efet) / orig) * 100).toFixed(2) : '0';
    return {
      _key: it.id || i + 1,
      produto_id: it.produto_id || null,
      codigo_produto: sv(it.produto_codigo_interno),
      descricao: it.descricao || '',
      unidade: it.unidade || 'UN',
      quantidade: sv(it.quantidade),
      preco_unitario: efet.toFixed(2),
      preco_original: (orig || efet).toFixed(2),
      desconto_perc: descPerc,
      subtotal: sv(it.subtotal),
      tipo: it.tipo_item || 'mercadoria',
      setor_id: null,
      setor_sigla: null,
      setor_nome: null,
    };
  });
}

// Resumo textual das formas de pagamento REALMENTE usadas na finalização
// (venda_pagamentos pode ter mais de uma forma; o pedido só guarda 1 campo texto).
function formaPagamentoResumo(pagamentos) {
  if (!pagamentos?.length) return '';
  return pagamentos.map(p => {
    const parc = p.parcelas > 1 ? ` ${p.parcelas}x` : '';
    return `${p.forma_pagamento}${parc}: ${fmtMoeda(p.valor)}`;
  }).join('  |  ');
}

/* ── Modal: Busca de Cliente ── */
function ModalBuscaCliente({ onSelect, onClose }) {
  const [busca, setBusca] = useState('');
  const [lista, setLista] = useState([]);
  const [sel, setSel]     = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    if (busca.length < 2) { setLista([]); return; }
    api.get('/clientes', { params: { busca, todos: 'true' } }).then(r => setLista(r.data)).catch(() => {});
  }, [busca]);

  return (
    <div className={s.overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={s.modal}>
        <div className={s.modalTitle}>👤 Buscar Cliente</div>
        <div className={s.modalBody}>
          <div className={s.modalSearchRow}>
            <input ref={inputRef} className={s.inputErp} style={{ flex: 1 }}
              placeholder="Nome, CPF/CNPJ, código..."
              value={busca} onChange={e => setBusca(e.target.value)}
              onKeyDown={e => e.key === 'Escape' && onClose()} />
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <table className={s.modalTabela}>
              <thead><tr><th>Cód.</th><th>Nome</th><th>CPF/CNPJ</th><th>Cidade</th></tr></thead>
              <tbody>
                {lista.length === 0 && (
                  <tr><td colSpan={4} style={{ padding: 10, color: '#888', textAlign: 'center' }}>
                    {busca.length < 2 ? 'Digite para buscar...' : 'Nenhum resultado.'}
                  </td></tr>
                )}
                {lista.map(c => (
                  <tr key={c.id} className={sel?.id === c.id ? s.selRow : ''}
                    onClick={() => setSel(c)} onDoubleClick={() => onSelect(c)}>
                    <td>{c.codigo}</td><td>{c.nome}</td>
                    <td>{c.cpf_cnpj || '—'}</td><td>{c.cidade || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className={s.modalFooter}>
          <button className={s.btnErp} onClick={() => sel && onSelect(sel)}>Selecionar</button>
          <button className={s.btnErp} onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

/* ── Modal: Busca de Pedido ── */
function ModalBuscaPedido({ onSelect, onClose }) {
  const [busca, setBusca]           = useState('');
  const [dataInicio, setDataInicio] = useState(hojeBR());
  const [dataFim, setDataFim]       = useState(hojeBR());
  const [lista, setLista]           = useState([]);
  const [sel, setSel]               = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    api.get('/vendas/pedidos', {
      params: {
        busca:       busca       || undefined,
        data_inicio: dataInicio  || undefined,
        data_fim:    dataFim     || undefined,
        limite: 200,
      },
    }).then(r => setLista(r.data)).catch(() => {});
  }, [busca, dataInicio, dataFim]);

  return (
    <div className={s.overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={s.modal} style={{ maxWidth: 820 }}>
        <div className={s.modalTitle}>🔍 Buscar Pedido / Orçamento</div>
        <div className={s.modalBody}>
          <div className={s.modalSearchRow} style={{ flexWrap: 'wrap', gap: 6 }}>
            <input ref={inputRef} className={s.inputErp} style={{ flex: 1, minWidth: 160 }}
              placeholder="Número, cliente..."
              value={busca} onChange={e => setBusca(e.target.value)}
              onKeyDown={e => e.key === 'Escape' && onClose()} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, whiteSpace: 'nowrap' }}>
              De:
              <InputDataFiltro className={s.inputErp} style={{ width: 130 }}
                value={dataInicio} onChange={e => setDataInicio(e.target.value)}
                onKeyDown={e => e.key === 'Escape' && onClose()} />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, whiteSpace: 'nowrap' }}>
              Até:
              <InputDataFiltro className={s.inputErp} style={{ width: 130 }}
                value={dataFim} onChange={e => setDataFim(e.target.value)}
                onKeyDown={e => e.key === 'Escape' && onClose()} />
            </label>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <table className={s.modalTabela}>
              <thead>
                <tr>
                  <th>#</th><th>Tipo</th><th>Data</th><th>Cliente</th>
                  <th>Tipo Mov.</th><th style={{ textAlign: 'right' }}>Total</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {lista.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: 10, color: '#888', textAlign: 'center' }}>
                    Nenhum pedido encontrado.
                  </td></tr>
                )}
                {lista.map(p => {
                  // Pedido já importado (virou venda): mostra a data/total
                  // REAIS da venda (pós-finalização, com desconto/acréscimo
                  // já aplicado) em vez do rascunho congelado do pedido —
                  // mesmo raciocínio de carregarPedido() ao abrir um só.
                  const jaVirouVenda = p.status === 'importado' && p.venda_id;
                  const dataExibida  = jaVirouVenda ? p.venda_criado_em : (p.data_emissao || p.criado_em);
                  const totalExibido = jaVirouVenda && p.venda_total != null ? p.venda_total : p.total;
                  return (
                    <tr key={p.id} className={sel?.id === p.id ? s.selRow : ''}
                      onClick={() => setSel(p)} onDoubleClick={() => onSelect(p)}>
                      <td style={{ fontWeight: 'bold' }}>
                        {p.id}{jaVirouVenda && <span style={{ fontWeight: 'normal', color: '#888' }}> → Venda #{p.venda_id}</span>}
                      </td>
                      <td>{p.tipo === 'orcamento' ? 'Orçamento' : 'Pedido'}</td>
                      <td>{fmtData(dataExibida)}</td>
                      <td>{p.cliente_nome || 'Consumidor Final'}</td>
                      <td>{p.tipo_movimento_nome || '—'}</td>
                      <td style={{ textAlign: 'right' }}>{fmtMoeda(totalExibido)}</td>
                      <td><span className={`${s.badge} ${badgeClass(p.status)}`}>
                        {STATUS_OPTS.find(o => o.v === p.status)?.l || p.status}
                      </span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className={s.modalFooter}>
          <button className={s.btnErp} onClick={() => sel && onSelect(sel)}>Selecionar</button>
          <button className={s.btnErp} onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   Modal Setor de Estoque
   ══════════════════════════════════════════════════════════ */
function ModalSetor({ produtoId, produtoNome, onConfirm, onClose }) {
  const [setores, setSetores] = useState([]);
  const [sel, setSel]         = useState(null);
  const [loading, setLoading] = useState(true);
  const tabelaRef = useRef(null);

  useEffect(() => {
    api.get(`/estoque/produto/${produtoId}/setores`)
      .then(r => { setSetores(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [produtoId]);

  useEffect(() => {
    const h = e => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'Enter' && sel) { e.preventDefault(); onConfirm(sel.setor_id); }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSel(prev => {
          const i = setores.findIndex(s => s.setor_id === prev?.setor_id);
          return setores[Math.min(i + 1, setores.length - 1)] || setores[0];
        });
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSel(prev => {
          const i = setores.findIndex(s => s.setor_id === prev?.setor_id);
          return setores[Math.max(i - 1, 0)] || setores[0];
        });
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [sel, setores, onConfirm, onClose]);

  const fmtQtd = v => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

  return (
    <div className={s.overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={s.modalSetor} onClick={e => e.stopPropagation()}>
        <div className={s.modalTitle} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Local de Saída — {produtoNome}</span>
          <button style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
            onClick={onClose}>✕</button>
        </div>
        <div className={s.modalBody} style={{ padding: '12px 16px' }}>
          {loading ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#888' }}>Carregando...</div>
          ) : (
            <table ref={tabelaRef} className={s.modalTabela}>
              <thead>
                <tr>
                  <th style={{ width: 60 }}>Sigla</th>
                  <th>Local de Estoque</th>
                  <th style={{ width: 120, textAlign: 'right' }}>Disponível</th>
                </tr>
              </thead>
              <tbody>
                {setores.length === 0 && (
                  <tr><td colSpan={3} style={{ textAlign: 'center', color: '#888', padding: 16 }}>
                    Nenhum local de estoque cadastrado.
                  </td></tr>
                )}
                {setores.map(se => (
                  <tr key={se.setor_id}
                    className={sel?.setor_id === se.setor_id ? s.selRow : ''}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSel(se)}
                    onDoubleClick={() => onConfirm(se.setor_id)}>
                    <td><strong style={{ fontFamily: 'monospace', color: '#1565c0' }}>{se.sigla}</strong></td>
                    <td>{se.nome}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'monospace',
                      color: parseFloat(se.quantidade) > 0 ? '#2e7d32' : '#999',
                      fontWeight: parseFloat(se.quantidade) > 0 ? 600 : 400 }}>
                      {fmtQtd(se.quantidade)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className={s.modalFooter}>
          <button className={s.btnErp} style={{ background: '#1565c0', color: '#fff' }}
            onClick={() => sel && onConfirm(sel.setor_id)} disabled={!sel}>
            Confirmar
          </button>
          <button className={s.btnErp} onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   Componente principal
   ══════════════════════════════════════════════════════════ */
export default function PedidosOrcamentos() {
  const [lista, setLista]             = useState([]);
  const [posicao, setPosicao]         = useState(0);
  const [form, setForm]               = useState({ ...FORM_VAZIO });
  const [modo, setModo]               = useState('nav');
  const [aba, setAba]                 = useState('itens');
  const [erroSalvar, setErroSalvar]   = useState('');
  const [salvando, setSalvando]       = useState(false);
  const [tiposMovimento, setTiposMovimento] = useState([]);
  const [setoresLista, setSetoresLista]     = useState([]);

  const [orgData, setOrgData] = useState(null);

  /* Modais */
  const [modalBusca, setModalBusca]     = useState(false);
  const [modalCliente, setModalCliente] = useState(false);
  /* null | 'consulta' | { mode: 'new' | 'edit', idx: number | null } — modo
     do F3 compartilhado (ModalBuscaProdutos), pra saber o que fazer com o
     produto escolhido (ou se é só consulta, sem seleção). */
  const [f3Modo, setF3Modo] = useState(null);
  const {
    modalF3, setModalF3, filtrosF3, setFiltrosF3, setFiltro,
    produtosF3, setProdutosF3, buscandoF3, idxF3, setIdxF3,
    buscaRapidaF3, setBuscaRapidaF3, buscaRapidaRef,
    fecharF3, buscarF3, handleBuscaRapidaF3, abrirF3,
  } = useBuscaProdutosF3({
    aoFechar: () => { if (f3Modo && f3Modo !== 'consulta') addItemBtnRef.current?.focus(); },
  });
  /* null | { tabela, clientePendente } */
  const [modalTabelaPreco, setModalTabelaPreco] = useState(null);
  // Tabela de preço do cliente atual (form.cliente_id) — usada pro F3 já
  // mostrar o preço certo e pra precificar itens novos lançados depois da
  // troca de cliente. null = cliente sem tabela (preço de cadastro normal).
  const [tabelaPrecoCliente, setTabelaPrecoCliente] = useState(null);
  /* null | { produtoId, produtoNome, itemKey } */
  const [modalSetor, setModalSetor] = useState(null);
  /* Finalizar venda direto do pedido */
  const [modalDocFiscal, setModalDocFiscal] = useState(null); // null | { vendaId }
  const [finalizando, setFinalizando]       = useState(false);
  const [cancelandoVenda, setCancelandoVenda] = useState(false);
  const [emitindoDoc, setEmitindoDoc]       = useState(false);
  const [resultadoDoc, setResultadoDoc]     = useState(null);
  const [larguraCupomVenda, setLarguraCupomVenda] = useState('80mm');
  const [itensServicoVenda, setItensServicoVenda] = useState([]); // itens_venda de serviço, p/ emitir NFS-e (1 DPS por item)
  const [nfseEmitindo, setNfseEmitindo]     = useState(null);
  const [nfseResultados, setNfseResultados] = useState({});
  const emitirNfseItem = async (item) => {
    if (!modalDocFiscal?.vendaId) return;
    setNfseEmitindo(item.id);
    // Aberta AQUI, antes de qualquer await — depois da chamada de rede o
    // navegador não considera mais "resposta direta a um clique" e bloqueia
    // o pop-up (achado ao vivo: só window.open() dentro das funções de
    // destino, chamadas depois do await, não bastava).
    const winDanfse = window.open('', '_blank');
    try {
      const { data } = await api.post('/fiscal/nfse', { venda_id: modalDocFiscal.vendaId, item_venda_id: item.id });
      setNfseResultados(prev => ({ ...prev, [item.id]: data }));
      if (data?.sucesso) await abrirDanfseOficial(data, orgData || {}, winDanfse);
      else if (data?.dados) await gerarPreviewDanfSe(data, orgData || {}, winDanfse);
      else { try { winDanfse?.close(); } catch (_) {} }
    } catch (err) {
      const data = err.response?.data || { erro: 'Erro ao gerar o DANFSe.' };
      setNfseResultados(prev => ({ ...prev, [item.id]: data }));
      // "Este item já possui NFS-e autorizada" — não é bem um erro, é a
      // proteção contra duplicar; reabre o DANFSe oficial já emitido em
      // vez de só mostrar o aviso sem nenhuma ação (senão não tinha como
      // reimprimir/rebaixar depois da 1ª vez).
      if (data?.documento_id) await abrirDanfseOficial({ ...data, sucesso: true }, orgData || {}, winDanfse);
      else if (data?.dados) await gerarPreviewDanfSe(data, orgData || {}, winDanfse);
      else { try { winDanfse?.close(); } catch (_) {} }
    } finally {
      setNfseEmitindo(null);
    }
  };
  const [modalPagamento, setModalPagamento] = useState(false); // pagamento estilo F9 do PDV
  /* null | { clienteId, faltando, retry } — cadastro incompleto p/ NF-e */
  const [modalCompletarCli, setModalCompletarCli] = useState(null);
  /* null | { vendaId, retry } — venda no VENDA CONSUMIDOR precisa de cliente p/ NF-e */
  const [modalSelecionarCli, setModalSelecionarCli] = useState(null);
  /* Correção fiscal de produtos (mesmo modal do PDV) */
  const [modalCorrecao, setModalCorrecao]       = useState(false);
  const [correcaoDados, setCorrecaoDados]       = useState(null);
  const [correcoes, setCorrecoes]               = useState({});
  const [salvandoCorrecao, setSalvandoCorrecao] = useState(false);
  const [correcaoTipo, setCorrecaoTipo]         = useState('nfe');
  const correcaoRetryRef = useRef(null);

  const abrirCorrecaoFiscal = (tipo, detalhes, retryFn) => {
    const inicial = {};
    (detalhes || []).forEach(d => { if (d.produto_id) inicial[d.produto_id] = {}; });
    setCorrecoes(inicial);
    setCorrecaoTipo(tipo);
    setCorrecaoDados({ detalhes_fiscais: detalhes || [] });
    correcaoRetryRef.current = retryFn;
    setModalCorrecao(true);
  };

  const salvarEReemitir = async () => {
    setSalvandoCorrecao(true);
    try {
      for (const [prodId, vals] of Object.entries(correcoes)) {
        if (Object.keys(vals).length > 0)
          await api.patch(`/produtos/${prodId}/fiscal`, vals);
      }
      setModalCorrecao(false);
      await correcaoRetryRef.current?.();
    } catch (err) {
      alert('Erro ao salvar correções: ' + (err.response?.data?.erro || err.message));
    } finally { setSalvandoCorrecao(false); }
  };
  /* Logs */
  const [modalLogs, setModalLogs]       = useState(false);
  const [logs, setLogs]             = useState([]);
  const [carregandoLogs, setCarregandoLogs] = useState(false);

  /* Refs para foco programático na grade */
  const itemRefs      = useRef({});   // { [_key]: { qtd, preco, desc } }
  const addItemBtnRef = useRef(null);
  const vendedorRef    = useRef(null);
  const [focusTarget, setFocusTarget] = useState(null);
  const tipoMovRef    = useRef(null); // mantém tipo_movimento atual acessível em closures

  /* ── Vendedor (Organização → Parâmetros → Vendas → "Solicitar vendedor")
        digita código (id do usuário) ou nome, resolve em "id - NOME" e grava
        vendedor_id de verdade — o mesmo usado em vendas/comissão. ── */
  const [usuariosVendedor, setUsuariosVendedor] = useState([]);
  useEffect(() => {
    api.get('/usuarios').then(r => setUsuariosVendedor(r.data || [])).catch(() => setUsuariosVendedor([]));
  }, []);

  const resolverVendedor = () => {
    const texto = (form.vendedor || '').trim();
    if (!texto) { setForm(f => ({ ...f, vendedor: '', vendedor_id: null })); return; }
    // Já resolvido (não mudou desde a última resolução) — não refaz a busca.
    if (form.vendedor_id && texto === `${form.vendedor_id} - ${usuariosVendedor.find(u => u.id === form.vendedor_id)?.nome || ''}`) return;

    let achado = null;
    if (/^\d+$/.test(texto)) {
      achado = usuariosVendedor.find(u => u.id === parseInt(texto));
    } else {
      const termo = texto.toLowerCase();
      const candidatos = usuariosVendedor.filter(u => u.nome?.toLowerCase().includes(termo));
      if (candidatos.length === 1) achado = candidatos[0];
    }
    if (achado) {
      setForm(f => ({ ...f, vendedor: `${achado.id} - ${achado.nome}`, vendedor_id: achado.id }));
    } else {
      setForm(f => ({ ...f, vendedor_id: null }));
    }
  };

  const keySeq   = useRef(1000);
  const editando = modo !== 'nav';

  /* ── Foco programático ── */
  useEffect(() => {
    if (!focusTarget) return;
    const { key, field } = focusTarget;
    const el = itemRefs.current[key]?.[field];
    if (el) { el.focus(); el.select?.(); }
    setFocusTarget(null);
  }, [focusTarget]);

  /* ── Org data ── */
  useEffect(() => {
    const u = getUsuarioSessao(); // sessão POR GUIA (cada guia pode estar numa organização)
    const orgId = u?.organizacao_id;
    if (orgId) api.get(`/organizacoes/${orgId}`).then(r => setOrgData(r.data)).catch(() => {});
  }, []);

  /* ── Tipos de movimento + setores + cliente padrão (1 = VENDA CONSUMIDOR) ── */
  const [clientePadrao, setClientePadrao] = useState(null);
  useEffect(() => {
    api.get('/fiscal/tipos-movimento').then(r => setTiposMovimento(r.data)).catch(() => {});
    api.get('/setores-estoque').then(r => setSetoresLista(r.data)).catch(() => {});
    api.get('/clientes/1').then(r => setClientePadrao(r.data)).catch(() => {});
  }, []);

  /* ── F3 global abre consulta de produtos; F4 inicia um novo pedido/orçamento
        (desde que nenhum modal esteja aberto) ── */
  useEffect(() => {
    const h = e => {
      if (e.key !== 'F3' && e.key !== 'F4') return;
      if (modalF3 || modalCliente || modalBusca || modalSetor || modalLogs || modalTabelaPreco || modalPagamento) return;
      e.preventDefault();
      if (e.key === 'F3') { setF3Modo('consulta'); abrirF3(); }
      else if (!editando) handleNovo();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });

  /* ── Navegação de teclado dentro do modal F3 (Escape/Enter/setas) — o
        componente compartilhado (ModalBuscaProdutos) não trata teclado
        sozinho, quem trata é a tela que o usa (mesmo padrão do PDV). ── */
  useEffect(() => {
    if (!modalF3) return;
    const h = e => {
      if (e.key === 'Escape') { fecharF3(); return; }
      if (e.key === 'Enter' && idxF3 !== null && f3Modo !== 'consulta') {
        e.preventDefault(); confirmarF3(produtosF3[idxF3]); return;
      }
      if (e.key === 'ArrowDown') { e.preventDefault(); setIdxF3(i => i === null ? 0 : Math.min(i + 1, produtosF3.length - 1)); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setIdxF3(i => i === null ? 0 : Math.max(i - 1, 0)); return; }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [modalF3, idxF3, produtosF3, f3Modo]); // eslint-disable-line

  /* ── F9: Salvar quando em edição; Finalizar Venda quando navegando ──
        (o modal de pagamento tem F9 próprio em capture phase) ── */
  useEffect(() => {
    const h = e => {
      if (e.key !== 'F9') return;
      if (modalF3 || modalCliente || modalBusca || modalSetor || modalLogs ||
          modalTabelaPreco || modalPagamento || modalDocFiscal) return;
      e.preventDefault();
      if (editando) {
        if (!salvando) handleSalvar();
        return;
      }
      if (form.id && ['aberto', 'aprovado'].includes(form.status) &&
          orgData?.finalizar_venda_pedido && form.itens.length && !finalizando) {
        finalizarVendaPedido();
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });

  /* ── Carregar lista ── */
  const carregarLista = async () => {
    try {
      const { data } = await api.get('/vendas/pedidos');
      setLista(data);
      return data;
    } catch { return []; }
  };

  useEffect(() => { carregarLista(); }, []); // eslint-disable-line

  async function carregarPedido(id, lst, pos) {
    try {
      const { data } = await api.get(`/vendas/pedidos/${id}`);
      itemRefs.current = {};
      let formData = pedidoParaForm(data);

      // Pedido já virou venda (status importado) — o rascunho do pedido fica
      // parado no que era ANTES de finalizar; busca a venda de verdade pra
      // mostrar o que realmente aconteceu (desconto final, cliente definido
      // na hora da NF-e, formas de pagamento usadas etc.), já que daqui pra
      // frente a tela é só consulta (edição fica bloqueada mesmo).
      if (data.status === 'importado') {
        try {
          const { data: ref } = await api.get(`/vendas/por-pedido/${id}`);
          const { data: v }   = await api.get(`/vendas/${ref.id}`);
          formData = {
            ...formData,
            venda_id:            v.id,
            // Data REAL da finalização, não a de criação do pedido — pedido
            // pode ter sido iniciado num dia e só finalizado (virado venda)
            // dias depois; tudo que essa tela mostra/imprime daqui pra
            // frente (inclusive os prints térmica/A4, que leem form.data_emissao)
            // passa a refletir quando a venda de fato aconteceu.
            data_emissao:        v.criado_em ? v.criado_em.slice(0, 10) : formData.data_emissao,
            cliente_id:          v.cliente_id || null,
            cliente_codigo:      sv(v.cliente_id),
            cliente_nome:        v.cliente_nome || formData.cliente_nome,
            cliente_cpf_cnpj:    v.cpf_cnpj || formData.cliente_cpf_cnpj,
            cliente_tipo_pessoa: v.cliente_tipo_pessoa || formData.cliente_tipo_pessoa,
            itens:               itensVendaParaForm(v.itens),
            desconto:            v.desconto ? sv(v.desconto) : '',
            forma_pagamento:     formaPagamentoResumo(v.pagamentos) || formData.forma_pagamento,
            condicao_pagamento:  '',
          };
        } catch { /* venda cancelada/excluída — mantém o snapshot do pedido */ }
      }

      setForm(formData);
      const useLst = lst || lista;
      const i = pos ?? useLst.findIndex(p => p.id === id);
      if (i >= 0) setPosicao(i);

      // Busca a tabela de preço do cliente do pedido — só pra itens NOVOS
      // que forem lançados daqui pra frente terem o preço certo; os itens
      // já salvos não são recalculados só por carregar o pedido.
      if (formData.cliente_id) {
        try {
          const { data: tabela } = await api.get(`/clientes/${formData.cliente_id}/tabela-preco`);
          setTabelaPrecoCliente(tabela || null);
        } catch { setTabelaPrecoCliente(null); }
      } else {
        setTabelaPrecoCliente(null);
      }
    } catch { setErroSalvar('Erro ao carregar pedido.'); }
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  /* ── Produto confirmado no F3 ── */
  const confirmarF3 = prod => {
    if (!f3Modo || f3Modo === 'consulta') return;
    const { mode, idx } = f3Modo;
    setModalF3(false);
    const tmf = tipoMovRef.current;
    const setorPadrao = tmf?.solicitar_setor ? null : (tmf?.setor_estoque_id || null);

    if (mode === 'new') {
      const key = ++keySeq.current;
      const preco = calcularPrecoTabela(prod, tabelaPrecoCliente);
      const newItem = {
        _key: key,
        produto_id:     prod.id,
        codigo_produto: sv(prod.codigo_interno),
        descricao:      prod.nome,
        unidade:        prod.unidade || 'UN',
        tipo:           prod.tipo || 'mercadoria',
        quantidade:     '1',
        preco_unitario: preco.toFixed(2),
        preco_original: preco.toFixed(2),
        desconto_perc:  '0',
        subtotal:       preco.toFixed(2),
        setor_id:       ehServico(prod.tipo) ? null : setorPadrao,
        setor_sigla:    null,
        setor_nome:     null,
      };
      setForm(f => ({ ...f, itens: [...f.itens, newItem] }));
      // Serviço não controla estoque — não pede local de estoque.
      if (tmf?.solicitar_setor && !ehServico(prod.tipo)) {
        setModalSetor({ produtoId: prod.id, produtoNome: prod.nome, itemKey: key });
      } else {
        setFocusTarget({ key, field: 'qtd' });
      }
    } else if (mode === 'edit' && idx !== null) {
      setForm(f => {
        const targetKey = f.itens[idx]?._key;
        const itens = f.itens.map((it, i) => {
          if (i !== idx) return it;
          const preco = calcularPrecoTabela(prod, tabelaPrecoCliente);
          const qtd   = parseFloat(it.quantidade) || 1;
          const desc  = parseFloat(it.desconto_perc) || 0;
          const efet  = +(preco * (1 - desc / 100)).toFixed(2);
          return {
            ...it,
            produto_id:     prod.id,
            codigo_produto: sv(prod.codigo_interno),
            descricao:      prod.nome,
            unidade:        prod.unidade || 'UN',
            tipo:           prod.tipo || 'mercadoria',
            preco_unitario: efet.toFixed(2),
            preco_original: preco.toFixed(2),
            subtotal:       (qtd * efet).toFixed(2),
            setor_id:       ehServico(prod.tipo) ? null : setorPadrao,
            setor_sigla:    null,
            setor_nome:     null,
          };
        });
        if (targetKey) {
          if (tmf?.solicitar_setor && !ehServico(prod.tipo)) {
            setTimeout(() => setModalSetor({ produtoId: prod.id, produtoNome: prod.nome, itemKey: targetKey }), 0);
          } else {
            setTimeout(() => setFocusTarget({ key: targetKey, field: 'qtd' }), 0);
          }
        }
        return { ...f, itens };
      });
    }
  };

  /* ── Setor confirmado ou modal fechado ── */
  const confirmarSetor = (setorId) => {
    const { itemKey } = modalSetor;
    const setor = setoresLista.find(s => s.id === setorId);
    setModalSetor(null);
    setForm(f => ({
      ...f,
      itens: f.itens.map(it => it._key === itemKey
        ? { ...it, setor_id: setorId, setor_sigla: setor?.sigla || null, setor_nome: setor?.nome || null }
        : it),
    }));
    setFocusTarget({ key: itemKey, field: 'qtd' });
  };

  const fecharModalSetor = () => {
    const { itemKey } = modalSetor;
    const tmf = tipoMovRef.current;
    const setorPadrao = tmf?.setor_estoque_id || null;
    const setor = setoresLista.find(s => s.id === setorPadrao);
    setModalSetor(null);
    setForm(f => ({
      ...f,
      itens: f.itens.map(it => it._key === itemKey
        ? { ...it, setor_id: setorPadrao, setor_sigla: setor?.sigla || null, setor_nome: setor?.nome || null }
        : it),
    }));
    setFocusTarget({ key: itemKey, field: 'qtd' });
  };

  /* ── Aplica cliente no form ── */
  const aplicarCliente = c => {
    setForm(f => ({
      ...f,
      cliente_id:       c.id,
      cliente_codigo:   sv(c.codigo),
      cliente_nome:     c.nome || '',
      cliente_cpf_cnpj: c.cpf_cnpj || '',
      cliente_tipo_pessoa: c.tipo_pessoa || 'F',
    }));
  };

  // Recalcula preco_unitario/preco_original/subtotal dos itens já lançados
  // pra refletir a NOVA tabela (ou o preço de cadastro normal, se novaTabela
  // for null) — busca o produto fresco de cada item (o formulário não guarda
  // o preço de cadastro "puro" depois que uma tabela já foi aplicada) e
  // preserva o desconto_perc de cada item (desconto manual continua valendo
  // em cima do preço novo, não é apagado nem ignorado). Devolve true se algum
  // preço mudou de verdade.
  const recalcularItensPorTabela = async novaTabela => {
    const itensComProduto = form.itens.filter(i => i.produto_id);
    if (!itensComProduto.length) return false;
    try {
      const frescos = await Promise.all(
        itensComProduto.map(i => api.get(`/produtos/${i.produto_id}`).then(r => r.data).catch(() => null))
      );
      const mapaFrescos = {};
      itensComProduto.forEach((i, idx) => { mapaFrescos[i.produto_id] = frescos[idx]; });

      let mudou = false;
      setForm(f => ({
        ...f,
        itens: f.itens.map(item => {
          const prod = mapaFrescos[item.produto_id];
          if (!prod) return item;
          const novaRef  = calcularPrecoTabela(prod, novaTabela);
          const refAtual = parseFloat(item.preco_original || item.preco_unitario) || 0;
          if (Math.abs(novaRef - refAtual) < 0.005) return item;
          mudou = true;
          const qtd  = parseFloat(item.quantidade) || 1;
          const desc = parseFloat(item.desconto_perc) || 0;
          const efet = +(novaRef * (1 - desc / 100)).toFixed(2);
          return {
            ...item,
            preco_unitario: efet.toFixed(2),
            preco_original: novaRef.toFixed(2),
            subtotal: (qtd * efet).toFixed(2),
          };
        }),
      }));
      return mudou;
    } catch {
      return false;
    }
  };

  /* ── Cliente selecionado: verifica tabela de preço ── */
  const onClienteSelect = async c => {
    setModalCliente(false);
    let novaTabela = null;
    try {
      const { data } = await api.get(`/clientes/${c.id}/tabela-preco`);
      novaTabela = data || null;
    } catch {}

    const temItens = form.itens.some(i => i.produto_id);
    if (temItens && novaTabela) {
      // Ganhando tabela nova com itens já lançados — pede confirmação
      // (pode ser uma mudança de preço grande, melhor o operador aprovar).
      setModalTabelaPreco({ tabela: novaTabela, clientePendente: c });
      return;
    }

    aplicarCliente(c);
    const tabelaAnterior = tabelaPrecoCliente;
    setTabelaPrecoCliente(novaTabela);
    if (temItens && !novaTabela && tabelaAnterior) {
      // Perdendo a tabela (cliente novo não tem, ou virou Venda Consumidor)
      // — reverte pro preço de cadastro automaticamente, sem perguntar.
      const mudou = await recalcularItensPorTabela(null);
      if (mudou) {
        alert('Preços dos itens já lançados foram atualizados para o preço de cadastro (cliente sem tabela de preço vinculada).');
      }
    }
  };

  /* ── Resposta do usuário sobre a tabela de preço ── */
  const confirmarTabelaPreco = async resposta => {
    const { tabela, clientePendente } = modalTabelaPreco;
    setModalTabelaPreco(null);
    aplicarCliente(clientePendente);
    setTabelaPrecoCliente(tabela);

    if (resposta === 'sim') {
      await recalcularItensPorTabela(tabela);
    }

    if (form.id) {
      const detalhe = resposta === 'sim' ? 'SIM — preços atualizados conforme tabela' : 'NÃO — preços mantidos';
      try {
        await api.post(`/vendas/pedidos/${form.id}/logs`, {
          tipo:        'atualizacao_preco_tabela',
          descricao:   `Cliente "${clientePendente.nome}" informado. Tabela "${tabela.descricao}" detectada. Resposta: ${detalhe}.`,
          resposta,
          tabela_id:   tabela.id,
          tabela_nome: tabela.descricao,
        });
        carregarLogs(form.id);
      } catch {}
    }
  };

  /* ── Carregar logs do pedido ── */
  const carregarLogs = async (id) => {
    const pedidoId = id ?? form.id;
    if (!pedidoId) return;
    setCarregandoLogs(true);
    try {
      const { data } = await api.get(`/vendas/pedidos/${pedidoId}/logs`);
      setLogs(Array.isArray(data) ? data : []);
    } catch { setLogs([]); }
    finally { setCarregandoLogs(false); }
  };

  const abrirLogs = () => {
    carregarLogs();
    setModalLogs(true);
  };

  /* ── Impressão do pedido ── */
  const BACKEND_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(/\/api$/, '');

  const fmtBRL = v => parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const fetchLogoBase64 = async () => {
    const raw = orgData?.logo_url || '';
    if (!raw) return '';
    const src = raw.startsWith('http') ? raw : `${BACKEND_URL}${raw}`;
    try {
      const resp = await fetch(src);
      if (!resp.ok) return '';
      const blob = await resp.blob();
      return await new Promise(res => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result);
        reader.onerror = () => res('');
        reader.readAsDataURL(blob);
      });
    } catch { return ''; }
  };

  const abrirPopupImpressao = async (html, largura, altura) => {
    const win = window.open('', '_blank', `width=${largura},height=${altura}`);
    if (!win) { alert('Permita pop-ups para imprimir.'); return; }
    win.document.write(html);
    win.document.close();
    fecharComEsc(win);
    await aguardarImagens(win);
    win.focus();
    win.print();
  };

  // Código do item nas impressões de Pedido/Orçamento — aqui o campo se
  // chama codigo_produto (não codigo_interno, nome diferente nesta tela).
  const codigoItemPedido = (i, idx) => i.codigo_produto || i.produto_id || (idx + 1);

  const imprimirTermica = async () => {
    if (!form.id) return;
    const logoData = await fetchLogoBase64();
    const logoHtml = logoData
      ? `<div style="text-align:center;margin-bottom:6px;"><img src="${logoData}" style="max-width:200px;max-height:80px;object-fit:contain;" /></div>`
      : '';
    const org = orgData || {};
    const nome = org.nome_fantasia || org.razao_social || 'EMPRESA';
    const cnpj = (org.cnpj || '').replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    const end  = [org.logradouro, org.numero, org.bairro].filter(Boolean).join(', ');
    const cid  = [org.cidade, org.uf].filter(Boolean).join('/');
    const fone = org.telefone ? `Tel: ${org.telefone}` : '';
    const total = form.itens.reduce((s, i) => s + parseFloat(i.subtotal || 0), 0);
    const desc  = parseFloat(form.desconto || 0);
    const frete = parseFloat(form.frete || 0);
    const totalFinal = Math.max(0, total - desc + frete);
    // Pedido já finalizado (status importado) imprime como VENDA de verdade —
    // com o número da venda, não do pedido/orçamento que deu origem a ela.
    const tipoLabel = form.status === 'importado' ? 'VENDA' : (form.tipo === 'orcamento' ? 'ORÇAMENTO' : 'PEDIDO DE VENDA');
    const numeroDoc = form.status === 'importado' ? (form.venda_id || form.id) : form.id;

    // Agrupamento Produtos x Serviços (parâmetro organizacoes.habilitar_servicos)
    const itemHtmlTermica = (i, idx) => `
  <div style="margin:2px 0;">
    <p><b>${codigoItemPedido(i, idx)}</b> ${i.descricao}</p>
    <p class="row">
      <span>${Number(i.quantidade).toLocaleString('pt-BR',{minimumFractionDigits:3})} ${i.unidade||'UN'} × R$ ${fmtBRL(parseFloat(i.preco_unitario))}</span>
      <span>R$ ${fmtBRL(parseFloat(i.subtotal))}</span>
    </p>
  </div>`;
    const somaSubtotal = arr => arr.reduce((s, i) => s + parseFloat(i.subtotal || 0), 0);
    const agruparTermica = !!org.habilitar_servicos && form.itens.some(i => ehServico(i.tipo));
    const itensHtmlTermica = !agruparTermica
      ? form.itens.map(itemHtmlTermica).join('')
      : (() => {
          const produtos = form.itens.filter(i => !ehServico(i.tipo));
          const servicos = form.itens.filter(i => ehServico(i.tipo));
          return `
  <p class="b" style="margin-top:2px;">📦 PRODUTOS</p>
  ${produtos.length ? produtos.map(itemHtmlTermica).join('') : '<p style="color:#888;">Nenhum produto.</p>'}
  <p class="row b"><span>Subtotal Produtos</span><span>R$ ${fmtBRL(somaSubtotal(produtos))}</span></p>
  <hr class="hr">
  <p class="b">🧾 SERVIÇOS</p>
  ${servicos.length ? servicos.map(itemHtmlTermica).join('') : '<p style="color:#888;">Nenhum serviço.</p>'}
  <p class="row b"><span>Subtotal Serviços</span><span>R$ ${fmtBRL(somaSubtotal(servicos))}</span></p>`;
        })();

    const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8">
<title>${tipoLabel} #${numeroDoc}</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box;}
  body{font-family:'Courier New',Courier,monospace;font-size:9px;font-weight:600;width:80mm;padding:4px;}
  .c{text-align:center;}.b{font-weight:bold;}.r{text-align:right;}
  .hr{border:none;border-top:1px dashed #000;margin:4px 0;}
  .row{display:flex;justify-content:space-between;}
  .grand{font-size:11px;}
  @media print{@page{margin:0;size:80mm auto;}}
</style></head><body>
  ${logoHtml}
  <p class="c b" style="font-size:12px;margin-bottom:2px;">${nome}</p>
  ${cnpj ? `<p class="c">CNPJ: ${cnpj}</p>` : ''}
  ${end   ? `<p class="c">${end}</p>` : ''}
  ${cid   ? `<p class="c">${cid}</p>` : ''}
  ${fone  ? `<p class="c">${fone}</p>` : ''}
  <hr class="hr">
  <p class="c b">${tipoLabel} Nº ${numeroDoc}</p>
  <p class="c">${fmtData(form.data_emissao)}</p>
  ${form.cliente_nome ? `<p class="row"><span>Cliente:</span><span>${form.cliente_nome}</span></p>` : ''}
  ${form.vendedor ? `<p class="row"><span>Vendedor:</span><span>${form.vendedor}</span></p>` : ''}
  <hr class="hr">
  <p class="b">ITENS</p>
  ${itensHtmlTermica}
  <hr class="hr">
  <p class="row"><span>Subtotal</span><span>R$ ${fmtBRL(total)}</span></p>
  ${agruparTermica ? `
  <p class="row" style="font-size:8px;color:#333;">&nbsp;&nbsp;<span>📦 Total Produtos</span><span>R$ ${fmtBRL(somaSubtotal(form.itens.filter(i => !ehServico(i.tipo))))}</span></p>
  <p class="row" style="font-size:8px;color:#333;">&nbsp;&nbsp;<span>🧾 Total Serviços</span><span>R$ ${fmtBRL(somaSubtotal(form.itens.filter(i => ehServico(i.tipo))))}</span></p>` : ''}
  ${desc  > 0 ? `<p class="row"><span>Desconto</span><span>– R$ ${fmtBRL(desc)}</span></p>` : ''}
  ${frete > 0 ? `<p class="row"><span>Frete</span><span>R$ ${fmtBRL(frete)}</span></p>` : ''}
  <p class="row grand b"><span>TOTAL</span><span>R$ ${fmtBRL(totalFinal)}</span></p>
  ${form.forma_pagamento ? `<hr class="hr"><p class="row"><span>Pagamento:</span><span>${form.forma_pagamento}</span></p>` : ''}
  ${form.observacoes ? `<hr class="hr"><p class="b">Obs:</p><p>${form.observacoes}</p>` : ''}
  <hr class="hr">
  <p class="c" style="font-size:8px;">Obrigado!</p>
</body></html>`;

    abrirPopupImpressao(html, 380, 700);
  };

  const imprimirProposta = async () => {
    if (!form.id) return;
    const win = window.open('', '_blank', 'width=900,height=750');
    if (!win) { alert('Permita pop-ups para imprimir.'); return; }
    win.document.write('<p style="font-family:Arial;padding:20px;">Gerando proposta...</p>');
    try {
      const { data } = await api.get(`/vendas/pedidos/${form.id}/proposta`);
      await imprimirPropostaComercial(data, win);
    } catch (err) {
      win.close();
      alert(err.response?.data?.erro || 'Erro ao gerar a proposta.');
    }
  };

  const imprimirA4 = async () => {
    if (!form.id) return;
    const logoData = await fetchLogoBase64();
    const logoHtml = logoData
      ? `<div style="margin-bottom:8px;"><img src="${logoData}" style="max-height:80px;max-width:200px;object-fit:contain;" /></div>`
      : '';
    const org = orgData || {};
    const nome = org.nome_fantasia || org.razao_social || 'EMPRESA';
    const cnpj = (org.cnpj || '').replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    const end  = [org.logradouro, org.numero, org.complemento, org.bairro].filter(Boolean).join(', ');
    const cid  = [org.cidade, org.uf].filter(Boolean).join('/');
    const fone = org.telefone ? `Tel: ${org.telefone}` : '';
    const total = form.itens.reduce((s, i) => s + parseFloat(i.subtotal || 0), 0);
    const desc  = parseFloat(form.desconto || 0);
    const frete = parseFloat(form.frete || 0);
    const totalFinal = Math.max(0, total - desc + frete);
    // Pedido já finalizado (status importado) imprime como VENDA de verdade —
    // com o número da venda, não do pedido/orçamento que deu origem a ela.
    const tipoLabel = form.status === 'importado' ? 'VENDA' : (form.tipo === 'orcamento' ? 'ORÇAMENTO' : 'PEDIDO DE VENDA');
    const numeroDoc = form.status === 'importado' ? (form.venda_id || form.id) : form.id;

    // Agrupamento Produtos x Serviços (parâmetro organizacoes.habilitar_servicos)
    const itemRowA4 = (i, idx) => `<tr>
        <td>${codigoItemPedido(i, idx)}</td>
        <td>${i.descricao}</td>
        <td class="c">${Number(i.quantidade).toLocaleString('pt-BR',{minimumFractionDigits:3})}</td>
        <td class="c">${i.unidade||'UN'}</td>
        <td class="r">R$ ${fmtBRL(parseFloat(i.preco_unitario))}</td>
        <td class="r">${parseFloat(i.desconto_perc||0).toFixed(2)}%</td>
        <td class="r">R$ ${fmtBRL(parseFloat(i.subtotal))}</td>
      </tr>`;
    const somaSubtotalA4 = arr => arr.reduce((s, i) => s + parseFloat(i.subtotal || 0), 0);
    const agruparA4 = !!org.habilitar_servicos && form.itens.some(i => ehServico(i.tipo));
    const linhasItensA4 = !agruparA4
      ? form.itens.map(itemRowA4).join('')
      : (() => {
          const produtos = form.itens.filter(i => !ehServico(i.tipo));
          const servicos = form.itens.filter(i => ehServico(i.tipo));
          const grupoRow = label => `<tr><td colspan="6" style="background:#eef2ff;color:#1e3a8a;font-weight:bold;padding:5px 8px;">${label}</td></tr>`;
          const vazioRow = label => `<tr><td colspan="6" style="text-align:center;color:#999;font-style:italic;">${label}</td></tr>`;
          const subtotalRow = (label, valor) => `<tr><td colspan="5" class="r" style="font-weight:bold;background:#f8fafc;">${label}</td><td class="r" style="font-weight:bold;background:#f8fafc;">R$ ${fmtBRL(valor)}</td></tr>`;
          return grupoRow('📦 PRODUTOS')
            + (produtos.length ? produtos.map(itemRowA4).join('') : vazioRow('Nenhum produto.'))
            + subtotalRow('Subtotal Produtos:', somaSubtotalA4(produtos))
            + grupoRow('🧾 SERVIÇOS')
            + (servicos.length ? servicos.map(itemRowA4).join('') : vazioRow('Nenhum serviço.'))
            + subtotalRow('Subtotal Serviços:', somaSubtotalA4(servicos));
        })();

    const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8">
<title>${tipoLabel} #${numeroDoc}</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box;}
  body{font-family:Arial,sans-serif;font-size:12pt;padding:20px;color:#111;}
  .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:12px;border-bottom:2px solid #333;padding-bottom:10px;}
  .org-info{flex:1;}
  .doc-info{text-align:right;min-width:180px;}
  .doc-info h2{font-size:16pt;color:#333;}
  table{width:100%;border-collapse:collapse;margin:10px 0;}
  th{background:#333;color:#fff;padding:5px 8px;text-align:left;font-size:11pt;}
  td{padding:4px 8px;border-bottom:1px solid #ddd;font-size:12pt;}
  .r{text-align:right;}.c{text-align:center;}
  .totais{width:260px;margin-left:auto;border-collapse:collapse;}
  .totais td{padding:3px 6px;border:none;font-size:12pt;}
  .totais .grand{font-weight:bold;font-size:15pt;border-top:2px solid #333;}
  .obs{border:1px solid #ccc;padding:8px;border-radius:4px;margin-top:10px;font-size:12pt;}
  .footer{margin-top:20px;border-top:1px solid #ccc;padding-top:8px;font-size:10pt;color:#777;text-align:center;}
  @media print{@page{margin:15mm;size:A4 portrait;}}
</style></head><body>
  <div class="header">
    <div class="org-info">
      ${logoHtml}
      <div style="font-weight:bold;font-size:14pt;">${nome}</div>
      ${cnpj ? `<div>CNPJ: ${cnpj}</div>` : ''}
      ${end  ? `<div>${end}</div>` : ''}
      ${cid  ? `<div>${cid}</div>` : ''}
      ${fone ? `<div>${fone}</div>` : ''}
    </div>
    <div class="doc-info">
      <h2>${tipoLabel}</h2>
      <div><b>Nº:</b> ${numeroDoc}</div>
      <div><b>Data:</b> ${fmtData(form.data_emissao)}</div>
      ${form.validade ? `<div><b>Validade:</b> ${fmtData(form.validade)}</div>` : ''}
      <div><b>Status:</b> ${form.status}</div>
    </div>
  </div>

  ${form.cliente_nome ? `<div style="margin-bottom:8px;padding:6px;background:#f5f5f5;border-radius:4px;">
    <b>Cliente:</b> ${form.cliente_nome}
    ${form.cliente_cpf_cnpj ? ` &nbsp;|&nbsp; <b>CPF/CNPJ:</b> ${form.cliente_cpf_cnpj}` : ''}
    ${form.vendedor ? ` &nbsp;|&nbsp; <b>Vendedor:</b> ${form.vendedor}` : ''}
  </div>` : ''}

  <table>
    <thead>
      <tr>
        <th style="width:8%">Cód.</th>
        <th style="width:32%">Descrição</th>
        <th class="c" style="width:10%">Qtd</th>
        <th class="c" style="width:8%">Un</th>
        <th class="r" style="width:14%">Preço Unit.</th>
        <th class="r" style="width:8%">Desc%</th>
        <th class="r" style="width:14%">Subtotal</th>
      </tr>
    </thead>
    <tbody>
      ${linhasItensA4}
    </tbody>
  </table>

  <table class="totais">
    <tr><td>Subtotal:</td><td class="r">R$ ${fmtBRL(total)}</td></tr>
    ${agruparA4 ? `
    <tr><td style="font-size:9px;color:#555;">&nbsp;&nbsp;📦 Total Produtos:</td><td class="r" style="font-size:9px;color:#555;">R$ ${fmtBRL(somaSubtotalA4(form.itens.filter(i => !ehServico(i.tipo))))}</td></tr>
    <tr><td style="font-size:9px;color:#555;">&nbsp;&nbsp;🧾 Total Serviços:</td><td class="r" style="font-size:9px;color:#555;">R$ ${fmtBRL(somaSubtotalA4(form.itens.filter(i => ehServico(i.tipo))))}</td></tr>` : ''}
    ${desc  > 0 ? `<tr><td>Desconto:</td><td class="r">– R$ ${fmtBRL(desc)}</td></tr>` : ''}
    ${frete > 0 ? `<tr><td>Frete:</td><td class="r">R$ ${fmtBRL(frete)}</td></tr>` : ''}
    <tr class="grand"><td>TOTAL:</td><td class="r">R$ ${fmtBRL(totalFinal)}</td></tr>
  </table>

  ${form.forma_pagamento ? `<div style="margin-top:8px;"><b>Forma de Pagamento:</b> ${form.forma_pagamento}${form.condicao_pagamento ? ` &nbsp;|&nbsp; <b>Condição:</b> ${form.condicao_pagamento}` : ''}</div>` : ''}
  ${form.prazo_entrega ? `<div><b>Prazo de Entrega:</b> ${form.prazo_entrega} dias</div>` : ''}
  ${form.observacoes ? `<div class="obs"><b>Observações:</b><br>${form.observacoes}</div>` : ''}

  <div class="footer">Documento gerado em ${new Date().toLocaleString('pt-BR')} — ${nome}</div>
</body></html>`;

    abrirPopupImpressao(html, 900, 900);
  };

  /* ── Adicionar item: abre F3 direto ── */
  const handleAddItem = () => {
    setF3Modo({ mode: 'new', idx: null });
    abrirF3();
  };

  /* ── Remover item ── */
  const remItem = idx => {
    setForm(f => {
      const key = f.itens[idx]?._key;
      if (key) delete itemRefs.current[key];
      return { ...f, itens: f.itens.filter((_, i) => i !== idx) };
    });
  };

  /* ── Atualizar campo de item (preço ⇄ desconto %, recalcula subtotal) ──
     Preço unitário é o efetivo; desconto % é relativo ao preço original:
     alterar um recalcula o outro (negativo = acréscimo). */
  const setItem = (idx, k, v) => {
    setForm(f => {
      const itens = f.itens.map((it, i) => {
        if (i !== idx) return it;
        const upd = { ...it, [k]: v };
        if (k === 'quantidade' || k === 'preco_unitario' || k === 'desconto_perc') {
          const orig = parseFloat(upd.preco_original) || 0;
          if (k === 'preco_unitario' && orig > 0) {
            const novo = parseFloat(v) || 0;
            upd.desconto_perc = String(+(((orig - novo) / orig) * 100).toFixed(2));
          } else if (k === 'desconto_perc' && orig > 0) {
            const d = parseFloat(v) || 0;
            upd.preco_unitario = (orig * (1 - d / 100)).toFixed(2);
          }
          const qtd   = parseFloat(upd.quantidade)     || 0;
          const preco = parseFloat(upd.preco_unitario) || 0;
          upd.subtotal = (qtd * preco).toFixed(2);
        }
        return upd;
      });
      return { ...f, itens };
    });
  };

  /* ── Tipo de movimento selecionado (com solicitar_setor, setor_estoque_id) ── */
  const tipoMovSelecionado = tiposMovimento.find(t => String(t.id) === String(form.tipo_movimento_fiscal_id)) || null;
  tipoMovRef.current = tipoMovSelecionado;

  /* Só os tipos marcados com "Mostrar em Pedidos/Orçamentos" aparecem no seletor;
     o tipo já gravado no pedido permanece visível mesmo se desmarcado depois. */
  const tiposMovimentoPedido = tiposMovimento.filter(t =>
    t.mostrar_em_pedidos !== false || String(t.id) === String(form.tipo_movimento_fiscal_id));

  /* ── Totais ── */
  const subTotal   = form.itens.reduce((acc, i) => acc + (parseFloat(i.subtotal) || 0), 0);
  const descVal    = parseFloat(form.desconto) || 0;
  const freteVal   = parseFloat(form.frete)    || 0;
  const totalGeral = Math.max(0, subTotal - descVal + freteVal);

  /* ── Agrupamento Produtos x Serviços (parâmetro organizacoes.habilitar_servicos) ── */
  const temServico       = form.itens.some(i => ehServico(i.tipo));
  const agrupar          = !!orgData?.habilitar_servicos && temServico;
  const subtotalProdutos = form.itens.filter(i => !ehServico(i.tipo)).reduce((acc, i) => acc + (parseFloat(i.subtotal) || 0), 0);
  const subtotalServicos = form.itens.filter(i => ehServico(i.tipo)).reduce((acc, i) => acc + (parseFloat(i.subtotal) || 0), 0);

  /* ── Finalizar venda direto do pedido — pagamento no modal estilo F9 do PDV ──
     Pedido em ABERTO com itens é aprovado automaticamente antes do pagamento. */
  const finalizarVendaPedido = async () => {
    if (!form.id || finalizando) return;
    if (!['aberto', 'aprovado'].includes(form.status)) {
      setErroSalvar('Só é possível finalizar venda de pedido ABERTO ou APROVADO.'); return;
    }
    if (!form.itens.length)         { setErroSalvar('Pedido sem itens.'); return; }
    if (freteVal > 0)               { setErroSalvar('Pedido com frete: finalize pelo PDV (F2 — Pré-venda).'); return; }
    if (orgData?.solicitar_vendedor_pedido && !form.vendedor_id) {
      setErroSalvar('Informe um vendedor válido (código ou nome) antes de finalizar.');
      vendedorRef.current?.focus();
      return;
    }
    setErroSalvar('');
    if (form.status === 'aberto') {
      try {
        await api.patch(`/vendas/pedidos/${form.id}/aprovar`);
        setForm(f => ({ ...f, status: 'aprovado' }));
      } catch (err) {
        setErroSalvar(err.response?.data?.erro || 'Erro ao aprovar o pedido.'); return;
      }
    }
    setModalPagamento(true);
  };

  const confirmarPagamentoPedido = async (dados) => {
    setFinalizando(true);
    try {
      const payload = {
        cliente_id: form.cliente_id || null,
        itens: form.itens.map(i => ({
          produto_id:     i.produto_id,
          descricao:      i.descricao,
          unidade:        i.unidade || 'UN',
          quantidade:     parseFloat(i.quantidade) || 0,
          preco_unitario: parseFloat(i.preco_unitario) || 0,
          preco_tabela:   parseFloat(i.preco_original) || parseFloat(i.preco_unitario) || 0,
          total_item:     parseFloat(i.subtotal) || 0,
          setor_id:       i.setor_id || null,
        })),
        pagamentos: dados.pagamentos,
        desconto:   parseFloat((descVal + (dados.desconto || 0)).toFixed(2)),
        total_pago: dados.total_pago,
        troco:      dados.troco,
        observacao: form.observacoes || null,
        vendedor_id: form.vendedor_id || null,
        tipo_movimento_fiscal_id: form.tipo_movimento_fiscal_id || null,
        pedido_origem_id: form.id,
      };
      let data;
      let tentativa = { ...payload };
      for (let i = 0; i < 6; i++) {
        try {
          ({ data } = await api.post('/vendas', tentativa));
          break;
        } catch (err) {
          const d = err.response?.data || {};
          if (d.exige_admin_desconto) {
            alert(d.erro);
            const senha = await pedirSenhaAdmin(
              'Desconto acima do máximo permitido nos produtos.',
              { acao: 'liberar_desconto_maximo', detalhes: d.erro });
            if (senha === null) return;
            tentativa = { ...tentativa, senha_admin_desconto: senha };
            continue;
          }
          if (d.exige_senha_estoque_insuficiente) {
            alert(d.erro);
            const senha = await pedirSenhaAdmin(
              'Estoque insuficiente para finalizar a venda.',
              { acao: 'liberar_estoque_insuficiente', detalhes: d.erro });
            if (senha === null) return;
            tentativa = { ...tentativa, senha_estoque_insuficiente: senha };
            continue;
          }
          if (d.exige_senha_limite_credito) {
            alert(d.erro);
            const senha = await pedirSenhaAdmin(
              'Limite de crédito do cliente insuficiente para esta venda a prazo.',
              { acao: 'liberar_limite_credito', detalhes: d.erro });
            if (senha === null) return;
            tentativa = { ...tentativa, senha_limite_credito: senha };
            continue;
          }
          if (d.exige_senha_atraso_cliente) {
            alert(d.erro);
            const senha = await pedirSenhaAdmin(
              'Cliente com títulos vencidos — esta venda também é a prazo.',
              { acao: 'liberar_cliente_inadimplente', detalhes: d.erro });
            if (senha === null) return;
            tentativa = { ...tentativa, senha_atraso_cliente: senha };
            continue;
          }
          if (d.aviso_atraso_cliente) {
            if (!window.confirm(`${d.erro}\n\nDeseja continuar mesmo assim?`)) return;
            tentativa = { ...tentativa, ciente_atraso_cliente: true };
            continue;
          }
          throw err;
        }
      }
      setModalPagamento(false);
      setResultadoDoc(null);
      setModalDocFiscal({ vendaId: data.id, boletos: data.boletos || [], aviso_boleto: data.aviso_boleto || null });
      setNfseResultados({});
      try {
        const { data: vendaCompleta } = await api.get(`/vendas/${data.id}`);
        setItensServicoVenda((vendaCompleta.itens || []).filter(i => ehServico(i.tipo_item)));
      } catch { setItensServicoVenda([]); }
      const lst = await carregarLista();
      await carregarPedido(form.id, lst);
    } finally { setFinalizando(false); }
  };

  /* Prévia (sem transmitir à SEFAZ) — venda já está salva, só ainda não foi
     emitida; dá pro operador conferir os dados antes de gastar uma emissão real. */
  const [gerandoPrevia, setGerandoPrevia] = useState(null); // null | 'nfce' | 'nfe'
  const previaDocVenda = async (tipo) => {
    if (!modalDocFiscal) return;
    setGerandoPrevia(tipo);
    try {
      if (tipo === 'nfce') {
        const { data: v } = await api.get(`/vendas/${modalDocFiscal.vendaId}`);
        await gerarPreviewDanfeNfceVenda(v, larguraCupomVenda, orgData || {});
      } else {
        await abrirDanfeNfePdf('/fiscal/nfe/danfe/preview', 'post', { venda_id: modalDocFiscal.vendaId });
      }
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao gerar a prévia.');
    } finally { setGerandoPrevia(null); }
  };

  const emitirDocVenda = async (tipo) => {
    if (!modalDocFiscal) return;
    setEmitindoDoc(true); setResultadoDoc(null);
    try {
      const endpoint = tipo === 'nfce' ? '/fiscal/nfce' : '/fiscal/nfe';
      const { data } = await api.post(endpoint, { venda_id: modalDocFiscal.vendaId });
      setResultadoDoc({ tipo, ...data });
      if (data.sucesso && tipo === 'nfce') {
        const { data: v } = await api.get(`/vendas/${modalDocFiscal.vendaId}`);
        gerarDanfeNfceVenda(v, data, larguraCupomVenda, orgData || {});
      }
      if (data.sucesso && tipo === 'nfe' && data.documento_id) {
        abrirDanfeNfePdf(`/fiscal/nfe/${data.documento_id}/danfe`);
      }
    } catch (err) {
      const d = err.response?.data || {};
      // Cadastro do cliente incompleto p/ NF-e → modal p/ completar e reemitir
      if (d.cliente_incompleto) {
        setModalCompletarCli({ clienteId: d.cliente_id, faltando: d.faltando || [], retry: () => emitirDocVenda(tipo) });
      }
      // Venda no VENDA CONSUMIDOR → seleciona o cliente na hora e reemite
      if (d.exige_cliente && modalDocFiscal?.vendaId) {
        setModalSelecionarCli({ vendaId: modalDocFiscal.vendaId, retry: () => emitirDocVenda(tipo) });
      }
      // Dados fiscais de PRODUTOS faltando → correção direto no cadastro
      const detFiscais = Array.isArray(d?.detalhes) && d.detalhes.length > 0
        && typeof d.detalhes[0] === 'object' ? d.detalhes : null;
      if (detFiscais) abrirCorrecaoFiscal(tipo, detFiscais, () => emitirDocVenda(tipo));
      setResultadoDoc({ tipo, sucesso: false, motivo: d.motivo || d.erro || err.message });
    } finally { setEmitindoDoc(false); }
  };

  /* EPEC: contingência quando a SEFAZ autorizadora está fora do ar */
  const emitirEpecVenda = async () => {
    if (!modalDocFiscal) return;
    const just = window.prompt(
      'Emissão em CONTINGÊNCIA (EPEC) — use apenas com a SEFAZ fora do ar.\n\n' +
      'Informe a justificativa (mínimo 15 caracteres):',
      'Indisponibilidade do web service da SEFAZ autorizadora'
    );
    if (just === null) return;
    if (just.trim().length < 15) { alert('Justificativa deve ter no mínimo 15 caracteres.'); return; }
    setEmitindoDoc(true); setResultadoDoc(null);
    try {
      const { data } = await api.post('/fiscal/nfe/epec', {
        venda_id: modalDocFiscal.vendaId, justificativa: just.trim(),
      });
      setResultadoDoc({ tipo: 'epec', ...data, motivo: 'EPEC registrado no Ambiente Nacional.' });
    } catch (err) {
      const d = err.response?.data || {};
      if (d.cliente_incompleto) {
        setModalCompletarCli({ clienteId: d.cliente_id, faltando: d.faltando || [], retry: () => emitirEpecVenda() });
      }
      if (d.exige_cliente && modalDocFiscal?.vendaId) {
        setModalSelecionarCli({ vendaId: modalDocFiscal.vendaId, retry: () => emitirEpecVenda() });
      }
      setResultadoDoc({ tipo: 'epec', sucesso: false, motivo: d.erro || err.message });
    } finally { setEmitindoDoc(false); }
  };

  const imprimirVendaFinalizada = async (tipo) => {
    if (!modalDocFiscal) return;
    try {
      const { data: v } = await api.get(`/vendas/${modalDocFiscal.vendaId}`);
      if (tipo === 'termica') await gerarCupomVenda(v, larguraCupomVenda, orgData || {});
      else await gerarA4Venda(v, orgData || {});
    } catch { alert('Erro ao buscar dados da venda.'); }
  };

  /* Teclas 1–4 escolhem a opção no modal de documento fiscal; Esc fecha */
  useEffect(() => {
    if (!modalDocFiscal) return;
    const h = e => {
      if (emitindoDoc) return;
      if (e.key === 'Escape') { e.preventDefault(); setModalDocFiscal(null); setResultadoDoc(null); setItensServicoVenda([]); return; }
      if (resultadoDoc?.sucesso) return;
      // Cliente pessoa jurídica: NFC-e não fica disponível para transmissão
      if (e.key === '1' && form.cliente_tipo_pessoa === 'J') return;
      const acoes = {
        '1': () => emitirDocVenda('nfce'),
        '2': () => emitirDocVenda('nfe'),
        '3': () => imprimirVendaFinalizada('termica'),
        '4': () => imprimirVendaFinalizada('a4'),
      };
      if (acoes[e.key]) { e.preventDefault(); acoes[e.key](); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });

  /* ── Modo Novo ── */
  const handleNovo = () => {
    setErroSalvar('');
    itemRefs.current = {};
    setTabelaPrecoCliente(null); // cliente padrão (Venda Consumidor) não tem tabela
    const venda = tiposMovimento.find(t => t.nome === 'Venda');
    setForm({
      ...FORM_VAZIO,
      data_emissao: hojeBR(),
      tipo_movimento_fiscal_id: venda ? sv(venda.id) : '',
      // Cliente padrão 1 = VENDA CONSUMIDOR (troque pelo cliente real quando
      // for crediário ou emissão de NF-e — ambos exigem cliente identificado)
      cliente_id:       clientePadrao?.id ?? 1,
      cliente_codigo:   sv(clientePadrao?.codigo ?? 1),
      cliente_nome:     clientePadrao?.nome || 'VENDA CONSUMIDOR',
      cliente_cpf_cnpj: clientePadrao?.cpf_cnpj || '',
      cliente_tipo_pessoa: clientePadrao?.tipo_pessoa || 'F',
    });
    setModo('novo');
    setAba('itens');
    setTimeout(() => {
      if (orgData?.solicitar_vendedor_pedido) vendedorRef.current?.focus();
      else addItemBtnRef.current?.focus();
    }, 80);
  };

  /* ── Modo Alterar ── */
  const handleAlterar = () => {
    if (!form.id) return;
    if (form.status === 'cancelado' || form.status === 'importado') {
      setErroSalvar('Pedido ' + form.status + ' não pode ser alterado.'); return;
    }
    setErroSalvar('');
    setModo('editar');
    setTimeout(() => addItemBtnRef.current?.focus(), 80);
  };

  /* ── Cancelar edição ── */
  const handleCancelar = () => {
    setErroSalvar('');
    itemRefs.current = {};
    setForm({ ...FORM_VAZIO });
    setTabelaPrecoCliente(null);
    setModo('nav');
    setAba('itens');
  };

  /* ── Excluir (cancelar pedido) ── */
  const handleExcluir = async () => {
    if (!form.id) return;
    if (!window.confirm(`Cancelar o pedido #${form.id}?`)) return;
    const senha = await pedirSenhaAdmin(
      `Cancelar o pedido #${form.id}.`,
      { acao: 'cancelar_pedido', detalhes: `Pedido #${form.id} — cliente: ${form.cliente_nome || '—'}` });
    if (senha === null) return;
    try {
      await api.patch(`/vendas/pedidos/${form.id}/cancelar`, { senha_admin: senha });
      await carregarLista();
      setForm({ ...FORM_VAZIO });
      setModo('nav');
      setAba('itens');
    } catch (err) { setErroSalvar(err.response?.data?.erro || 'Erro ao cancelar.'); }
  };

  /* ── Cancelar a VENDA gerada por este pedido (faturado/importado) ──
     Devolve estoque e desfaz o financeiro; doc fiscal autorizado é cancelado
     na SEFAZ com motivo (padrão "Cancelado pelo cliente"). Prazo da SEFAZ
     excedido ou recebimento já efetivado → sistema orienta a devolução. */
  const cancelarVendaDoPedido = async () => {
    if (!form.id || form.status !== 'importado') return;
    setErroSalvar('');
    let venda;
    try {
      ({ data: venda } = await api.get(`/vendas/por-pedido/${form.id}`));
    } catch (err) {
      setErroSalvar(err.response?.data?.erro || 'Venda deste pedido não encontrada.');
      return;
    }
    let motivo = null;
    if (venda.doc_fiscal_status === 'autorizada') {
      const tipoDoc = venda.doc_fiscal_tipo === 'nfe' ? 'NF-e' : 'NFC-e';
      motivo = window.prompt(
        `Cancelar a venda #${venda.id} e a ${tipoDoc} nº ${venda.doc_fiscal_numero || ''} na SEFAZ?\n\n` +
        `O estoque será devolvido, o financeiro desfeito e o pedido reaberto.\n` +
        `Motivo do cancelamento (mínimo 15 caracteres):`,
        'Cancelado pelo cliente'
      );
      if (motivo === null) return;
      if (motivo.trim().length < 15) { alert('Motivo deve ter no mínimo 15 caracteres.'); return; }
    } else {
      motivo = window.prompt(
        `Cancelar a venda #${venda.id} deste pedido?\n\nO estoque será devolvido, o financeiro desfeito e o pedido reaberto.\n\n` +
        `Motivo do cancelamento (mínimo 5 caracteres):`
      );
      if (motivo === null) return;
      if (motivo.trim().length < 5) { alert('Motivo deve ter no mínimo 5 caracteres.'); return; }
    }
    const senha = await pedirSenhaAdmin(
      `Cancelar a venda #${venda.id} (pedido #${form.id}).`,
      { acao: 'cancelar_venda', detalhes: `Venda #${venda.id} do pedido #${form.id}. Motivo: ${motivo.trim()}` });
    if (senha === null) return;
    setCancelandoVenda(true);
    try {
      const { data } = await api.patch(`/vendas/${venda.id}/cancelar`, {
        senha_admin: senha,
        motivo: motivo.trim(),
      });
      alert(data.mensagem || 'Venda cancelada.');
      const lst = await carregarLista();
      await carregarPedido(form.id, lst);
    } catch (err) {
      const d = err.response?.data || {};
      const msg = d.erro || 'Erro ao cancelar a venda.';
      if (d.prazo_excedido || d.exige_devolucao) alert(msg);
      setErroSalvar(msg);
    } finally {
      setCancelandoVenda(false);
    }
  };

  /* ── Salvar ── */
  const handleSalvar = async () => {
    setErroSalvar('');
    const itensValidos = form.itens.filter(i => i.descricao?.trim());
    if (!itensValidos.length) {
      setErroSalvar('Adicione ao menos um item.'); return;
    }
    // Com finalizar_venda_pedido ativo o pagamento é capturado no modal da finalização
    if (form.status === 'aprovado' && !form.forma_pagamento && !orgData?.finalizar_venda_pedido) {
      setErroSalvar('Pedido aprovado exige forma de pagamento. Preencha na aba Financeiro.');
      setAba('financeiro');
      return;
    }
    if (orgData?.solicitar_vendedor_pedido && !form.vendedor_id) {
      setErroSalvar('Informe um vendedor válido (código ou nome) antes de salvar.');
      vendedorRef.current?.focus();
      return;
    }
    setSalvando(true);
    try {
      const payload = {
        tipo:                     form.tipo,
        status:                   form.status,
        cliente_id:               form.cliente_id || null,
        tipo_movimento_fiscal_id: form.tipo_movimento_fiscal_id ? parseInt(form.tipo_movimento_fiscal_id) : null,
        data_emissao:             form.data_emissao || null,
        validade:                 form.validade || null,
        vendedor:                 form.vendedor || null,
        vendedor_id:              form.vendedor_id || null,
        forma_pagamento:          form.forma_pagamento || null,
        condicao_pagamento:       form.condicao_pagamento || null,
        desconto:                 parseFloat(form.desconto) || 0,
        frete:                    parseFloat(form.frete) || 0,
        tipo_frete:               form.tipo_frete || '9',
        transportadora:           form.transportadora || null,
        prazo_entrega:            form.prazo_entrega ? parseInt(form.prazo_entrega) : null,
        observacoes:              form.observacoes || null,
        ...(orgData?.proposta_comercial_ativa ? { proposta: form.proposta || {} } : {}),
        itens: itensValidos.map(i => ({
          produto_id:     i.produto_id || null,
          codigo_produto: i.codigo_produto || null,
          descricao:      i.descricao,
          unidade:        i.unidade || 'UN',
          quantidade:     parseFloat(i.quantidade) || 1,
          preco_unitario: parseFloat(i.preco_unitario) || 0,
          desconto_perc:  parseFloat(i.desconto_perc) || 0,
          subtotal:       parseFloat(i.subtotal) || 0,
          setor_id:       i.setor_id || null,
        })),
      };
      let saved;
      if (modo === 'novo') {
        const { data } = await api.post('/vendas/pedidos', payload);
        saved = data;
      } else {
        const { data } = await api.put(`/vendas/pedidos/${form.id}`, payload);
        saved = data;
      }
      const data = await carregarLista();
      const i = data.findIndex(p => p.id === saved.id);
      await carregarPedido(saved.id, data, i >= 0 ? i : 0);
      setModo('nav');
    } catch (err) {
      setErroSalvar(err.response?.data?.erro || 'Erro ao salvar.');
    } finally { setSalvando(false); }
  };

  /* ── Navegação ── */
  const navAnterior = () => {
    if (posicao > 0) { const n = posicao - 1; setPosicao(n); carregarPedido(lista[n].id, lista, n); }
  };
  const navProximo = () => {
    if (posicao < lista.length - 1) { const n = posicao + 1; setPosicao(n); carregarPedido(lista[n].id, lista, n); }
  };

  /* ── Status bar ── */
  const statusText = () => {
    if (erroSalvar) return <span className={s.statusErro}>⚠ {erroSalvar}</span>;
    if (modo === 'novo')   return <span className={s.statusNovo}>INSERINDO NOVO PEDIDO</span>;
    if (modo === 'editar') return <span className={s.statusEditar}>ALTERANDO PEDIDO #{form.id}</span>;
    if (form.id) {
      const stLabel = STATUS_OPTS.find(o => o.v === form.status)?.l || form.status;
      return `Pedido #${form.id} — ${stLabel} | ${lista.length} registro(s) | Pos: ${posicao + 1}`;
    }
    return 'Nenhum pedido. Clique em "Novo" para criar.';
  };

  const aprovadoSemPgto = form.status === 'aprovado' && !form.forma_pagamento && !orgData?.finalizar_venda_pedido;

  /* ═══ RENDER ═══ */
  return (
    <>
      <div className={s.erpWrap}>

        {/* Título */}
        <div className={s.titleBar}>
          <span>📄</span> Pedidos / Orçamentos
        </div>

        {/* ── Cabeçalho linha 1 ── */}
        <div className={s.headerRow}>
          <div className={s.headerGroup}>
            <span className={s.headerLabel}>Nº</span>
            <input className={s.inputErp} style={{ width: 60 }}
              value={form.id || ''} readOnly placeholder="auto" />
          </div>
          <div className={s.headerGroup}>
            <span className={s.headerLabel}>Tipo</span>
            <select className={s.selectErp} style={{ width: 140 }}
              value={form.tipo} disabled={!editando}
              onChange={e => set('tipo', e.target.value)}>
              {TIPO_OPTS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
          <div className={s.headerGroup}>
            <span className={s.headerLabel}>Data</span>
            <input className={s.inputErp} type="date" style={{ width: 120 }}
              value={form.data_emissao} readOnly={!editando}
              onChange={e => set('data_emissao', e.target.value)} />
          </div>
          <div className={s.headerGroup}>
            <span className={s.headerLabel}>Validade</span>
            <input className={s.inputErp} type="date" style={{ width: 120 }}
              value={form.validade} readOnly={!editando}
              onChange={e => set('validade', e.target.value)} />
          </div>
          <div className={s.headerGroup}>
            <span className={s.headerLabel}>Status</span>
            <select className={s.selectErp} style={{ width: 100 }}
              value={form.status} disabled={!editando}
              onChange={e => set('status', e.target.value)}>
              {STATUS_OPTS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
          {form.status === 'importado' && form.venda_id && (
            <div className={s.headerGroup}>
              <span className={s.headerLabel}>Venda</span>
              <input className={s.inputErp} style={{ width: 70, fontWeight: 'bold' }}
                value={`#${form.venda_id}`} readOnly
                title="Este pedido foi finalizado como esta venda — data/total exibidos aqui já são os reais da venda" />
            </div>
          )}
          <div className={s.headerGroup}>
            <span className={s.headerLabel}>Vendedor</span>
            <input ref={vendedorRef} className={s.inputErp} style={{ width: 130 }}
              value={form.vendedor} readOnly={!editando}
              placeholder="cód. ou nome"
              title="Digite o código (id do usuário) ou o nome e saia do campo (Tab/clique fora) para resolver"
              onChange={e => set('vendedor', e.target.value)}
              onBlur={resolverVendedor}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  resolverVendedor();
                  setTimeout(() => addItemBtnRef.current?.focus(), 0);
                }
              }} />
            {editando && orgData?.solicitar_vendedor_pedido && !form.vendedor_id && (
              <span style={{ fontSize: 11, color: '#b91c1c', marginLeft: 6 }}>obrigatório</span>
            )}
          </div>
        </div>

        {/* ── Cabeçalho linha 2: Cliente + Tipo de Movimento ── */}
        <div className={s.headerRow} style={{ background: '#f4f8ff' }}>
          <div className={s.headerGroup}>
            <span className={s.headerLabel}>Cliente</span>
            <input className={s.inputErp} style={{ width: 65 }}
              value={form.cliente_codigo} readOnly={!editando}
              placeholder="cód."
              onChange={e => set('cliente_codigo', e.target.value)} />
            {editando && (
              <button className={s.btnLookup} type="button"
                title="Buscar cliente" onClick={() => setModalCliente(true)}>…</button>
            )}
            <input className={s.inputErp} style={{ width: 240 }}
              value={form.cliente_nome} readOnly placeholder="— Consumidor Final —" />
            <input className={s.inputErp} style={{ width: 140 }}
              value={form.cliente_cpf_cnpj} readOnly placeholder="CPF/CNPJ" />
          </div>
          <div className={s.headerDivider} />
          <div className={s.headerGroup} style={{ flex: 1 }}>
            <span className={s.headerLabel}>Tipo de Movimento</span>
            <select className={s.selectErp} style={{ flex: 1, minWidth: 200 }}
              value={form.tipo_movimento_fiscal_id} disabled={!editando}
              onChange={e => set('tipo_movimento_fiscal_id', e.target.value)}>
              <option value="">— nenhum —</option>
              {tiposMovimentoPedido.map(t => (
                <option key={t.id} value={t.id}>
                  {t.nome} — CFOP {t.cfop_dentro_estado_id}/{t.cfop_fora_estado_id}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ── Abas ── */}
        <div className={s.tabBar}>
          {(orgData?.proposta_comercial_ativa ? [...ABAS, ABA_PROPOSTA] : ABAS).map(a => (
            <button key={a.id} type="button"
              className={`${s.tab} ${aba === a.id ? s.tabAtiva : ''}`}
              onClick={() => setAba(a.id)}>
              {a.label}
              {a.id === 'financeiro' && aprovadoSemPgto && editando && (
                <span style={{ color: '#c00', marginLeft: 4, fontWeight: 'bold' }}>⚠</span>
              )}
            </button>
          ))}
        </div>

        {/* ── Conteúdo das abas ── */}
        <div className={s.tabContent}>

          {/* ─── ABA ITENS ─── */}
          {aba === 'itens' && (
            <div>
              {editando && (
                <div className={s.itensTool}>
                  <button
                    ref={addItemBtnRef}
                    className={`${s.btnErp} ${s.btnErpPrimario}`}
                    type="button"
                    onClick={handleAddItem}>
                    + Adicionar Item
                  </button>
                </div>
              )}

              <table className={s.itensGrid}>
                <thead>
                  <tr>
                    <th className={s.tdSeq}>#</th>
                    <th className={s.tdCod}>Cód.</th>
                    <th className={s.tdBtn}></th>
                    <th className={s.tdLoc}>Loc.</th>
                    <th className={s.tdDesc}>Descrição do Produto / Serviço</th>
                    <th className={s.tdUn}>Un</th>
                    <th className={s.tdQtd}>Quantidade</th>
                    <th className={s.tdPreco}>Preço Unit.</th>
                    <th className={s.tdDesc_}>Desc. %</th>
                    <th className={s.tdSub}>Subtotal</th>
                    {editando && <th className={s.tdDel}></th>}
                  </tr>
                </thead>
                <tbody>
                  {form.itens.length === 0 && (
                    <tr>
                      <td colSpan={editando ? 11 : 10}
                        style={{ textAlign: 'center', color: '#888', padding: 20, fontStyle: 'italic' }}>
                        {editando ? 'Clique em "+ Adicionar Item" para inserir produtos.' : 'Nenhum item.'}
                      </td>
                    </tr>
                  )}

                  {(() => {
                    const renderLinha = (item, idx) => (
                    <tr key={item._key}>
                      <td className={s.tdSeq}>{idx + 1}</td>

                      {/* Código com busca por blur */}
                      <td className={s.tdCod}>
                        <input className={s.cellInput}
                          value={item.codigo_produto} readOnly={!editando}
                          onChange={e => setItem(idx, 'codigo_produto', e.target.value)}
                          onBlur={async e => {
                            const cod = e.target.value.trim();
                            if (!cod || !editando) return;
                            try {
                              const { data } = await api.get('/produtos', { params: { busca: cod, limite: 1 } });
                              if (data[0]) {
                                const p = data[0];
                                const preco = parseFloat(p.preco_venda) || 0;
                                const qtd   = parseFloat(item.quantidade) || 1;
                                const desc  = parseFloat(item.desconto_perc) || 0;
                                const efet  = +(preco * (1 - desc / 100)).toFixed(2);
                                const tmf   = tipoMovRef.current;
                                const setorPadrao = tmf?.solicitar_setor ? null : (tmf?.setor_estoque_id || null);
                                const setorInfo = setoresLista.find(s => s.id === setorPadrao);
                                setForm(f => ({
                                  ...f,
                                  itens: f.itens.map((it, i) => i !== idx ? it : {
                                    ...it, produto_id: p.id,
                                    codigo_produto: sv(p.codigo_interno),
                                    descricao: p.nome, unidade: p.unidade || 'UN',
                                    tipo: p.tipo || 'mercadoria',
                                    preco_unitario: efet.toFixed(2),
                                    preco_original: preco.toFixed(2),
                                    subtotal: (qtd * efet).toFixed(2),
                                    setor_id: ehServico(p.tipo) ? null : setorPadrao,
                                    setor_sigla: ehServico(p.tipo) ? null : (setorInfo?.sigla || null),
                                    setor_nome: ehServico(p.tipo) ? null : (setorInfo?.nome || null),
                                  }),
                                }));
                                // Serviço não controla estoque — não pede local de estoque.
                                if (tmf?.solicitar_setor && !ehServico(p.tipo)) {
                                  setTimeout(() => setModalSetor({ produtoId: p.id, produtoNome: p.nome, itemKey: item._key }), 80);
                                }
                              }
                            } catch {}
                          }}
                          placeholder="código" />
                      </td>

                      {/* Botão F3 */}
                      <td className={s.tdBtn}>
                        {editando && (
                          <button className={s.btnLookup} type="button"
                            title="F3 — Buscar produto"
                            onClick={() => { setF3Modo({ mode: 'edit', idx }); abrirF3(); }}>
                            …
                          </button>
                        )}
                      </td>

                      {/* Local de estoque — serviço não controla estoque, não pede local */}
                      <td className={s.tdLoc}>
                        {ehServico(item.tipo) ? (
                          <span style={{ color: '#ccc', fontSize: 11 }} title="Serviço não controla estoque">—</span>
                        ) : item.setor_id ? (
                          <span
                            className={s.setorBadge}
                            title={item.setor_nome || ''}
                            style={{ cursor: editando && item.produto_id ? 'pointer' : 'default' }}
                            onClick={() => editando && item.produto_id &&
                              setModalSetor({ produtoId: item.produto_id, produtoNome: item.descricao, itemKey: item._key })}>
                            {item.setor_sigla || setoresLista.find(s => s.id === item.setor_id)?.sigla || '?'}
                          </span>
                        ) : editando && item.produto_id ? (
                          <span
                            className={s.setorBadgeVazio}
                            title="Clique para selecionar setor"
                            onClick={() => setModalSetor({ produtoId: item.produto_id, produtoNome: item.descricao, itemKey: item._key })}>
                            —
                          </span>
                        ) : <span style={{ color: '#ccc', fontSize: 11 }}>—</span>}
                      </td>

                      <td className={s.tdDesc}>
                        <input className={s.cellInput}
                          value={item.descricao} readOnly={!editando}
                          onChange={e => setItem(idx, 'descricao', e.target.value)}
                          placeholder="descrição" />
                      </td>

                      <td className={s.tdUn}>
                        <input className={s.cellInput}
                          value={item.unidade} readOnly={!editando} maxLength={6}
                          onChange={e => setItem(idx, 'unidade', e.target.value)} />
                      </td>

                      {/* Quantidade → Enter → foca Preço */}
                      <td className={s.tdQtd}>
                        <input
                          ref={el => {
                            if (!itemRefs.current[item._key]) itemRefs.current[item._key] = {};
                            itemRefs.current[item._key].qtd = el;
                          }}
                          className={`${s.cellInput} ${s.cellInputNum}`}
                          type="number" min="0.001" step="any"
                          value={item.quantidade} readOnly={!editando}
                          onChange={e => setItem(idx, 'quantidade', e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              setFocusTarget({ key: item._key, field: 'preco' });
                            }
                          }} />
                      </td>

                      {/* Preço → Enter → foca Desconto */}
                      <td className={s.tdPreco}>
                        <input
                          ref={el => {
                            if (!itemRefs.current[item._key]) itemRefs.current[item._key] = {};
                            itemRefs.current[item._key].preco = el;
                          }}
                          className={`${s.cellInput} ${s.cellInputNum}`}
                          type="number" min="0" step="0.01"
                          value={item.preco_unitario} readOnly={!editando}
                          onChange={e => setItem(idx, 'preco_unitario', e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              setFocusTarget({ key: item._key, field: 'desc' });
                            }
                          }} />
                      </td>

                      {/* Desconto % → Enter → foca botão Adicionar Item */}
                      <td className={s.tdDesc_}>
                        <input
                          ref={el => {
                            if (!itemRefs.current[item._key]) itemRefs.current[item._key] = {};
                            itemRefs.current[item._key].desc = el;
                          }}
                          className={`${s.cellInput} ${s.cellInputNum}`}
                          type="number" max="100" step="0.01"
                          title="Positivo = desconto · Negativo = acréscimo"
                          value={item.desconto_perc} readOnly={!editando}
                          onChange={e => setItem(idx, 'desconto_perc', e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addItemBtnRef.current?.focus();
                              setTimeout(() => handleAddItem(), 40);
                            }
                          }} />
                      </td>

                      <td className={s.tdSub}>
                        <input className={`${s.cellInput} ${s.cellInputNum}`}
                          type="number" min="0" step="0.01"
                          value={item.subtotal} readOnly={!editando}
                          onChange={e => setItem(idx, 'subtotal', e.target.value)} />
                      </td>

                      {editando && (
                        <td className={s.tdDel}>
                          <button className={`${s.btnErp} ${s.btnErpSmall} ${s.btnErpPerigo}`}
                            type="button" title="Remover"
                            onClick={() => remItem(idx)}>✕</button>
                        </td>
                      )}
                    </tr>
                    );

                    const comIdx = form.itens.map((item, idx) => ({ item, idx }));
                    if (!agrupar) return comIdx.map(({ item, idx }) => renderLinha(item, idx));

                    const produtos = comIdx.filter(({ item }) => !ehServico(item.tipo));
                    const servicos = comIdx.filter(({ item }) => ehServico(item.tipo));
                    const grupoHeader   = { textAlign: 'left', fontWeight: 700, padding: '5px 8px', background: '#eef2ff', color: '#1e3a8a' };
                    const grupoVazio    = { textAlign: 'center', color: '#aaa', fontStyle: 'italic', padding: 8 };
                    const subtotalLabel = { textAlign: 'right', fontWeight: 700, background: '#f8fafc', padding: '4px 8px' };
                    const subtotalValor = { fontWeight: 700, background: '#f8fafc', padding: '4px 8px' };
                    return (
                      <React.Fragment>
                        <tr><td colSpan={editando ? 11 : 10} style={grupoHeader}>📦 Produtos</td></tr>
                        {produtos.length
                          ? produtos.map(({ item, idx }) => renderLinha(item, idx))
                          : <tr><td colSpan={editando ? 11 : 10} style={grupoVazio}>Nenhum produto.</td></tr>}
                        <tr>
                          <td colSpan={9} style={subtotalLabel}>Subtotal Produtos:</td>
                          <td colSpan={editando ? 2 : 1} style={subtotalValor}>{fmtMoeda(subtotalProdutos)}</td>
                        </tr>
                        <tr><td colSpan={editando ? 11 : 10} style={grupoHeader}>🧾 Serviços</td></tr>
                        {servicos.length
                          ? servicos.map(({ item, idx }) => renderLinha(item, idx))
                          : <tr><td colSpan={editando ? 11 : 10} style={grupoVazio}>Nenhum serviço.</td></tr>}
                        <tr>
                          <td colSpan={9} style={subtotalLabel}>Subtotal Serviços:</td>
                          <td colSpan={editando ? 2 : 1} style={subtotalValor}>{fmtMoeda(subtotalServicos)}</td>
                        </tr>
                      </React.Fragment>
                    );
                  })()}
                </tbody>
              </table>

              <div className={s.itensTotais}>
                <div className={s.totaisBox}>
                  <div className={s.totaisLinha}>
                    <span>Subtotal dos Itens:</span><span>{fmtMoeda(subTotal)}</span>
                  </div>
                  {agrupar && (
                    <>
                      <div className={s.totaisLinha} style={{ fontSize: 12, color: '#444' }}>
                        <span>&nbsp;&nbsp;📦 Total Produtos:</span><span>{fmtMoeda(subtotalProdutos)}</span>
                      </div>
                      <div className={s.totaisLinha} style={{ fontSize: 12, color: '#444' }}>
                        <span>&nbsp;&nbsp;🧾 Total Serviços:</span><span>{fmtMoeda(subtotalServicos)}</span>
                      </div>
                    </>
                  )}
                  {descVal > 0 && (
                    <div className={s.totaisLinha}>
                      <span>Desconto Geral:</span>
                      <span style={{ color: '#c00' }}>- {fmtMoeda(descVal)}</span>
                    </div>
                  )}
                  {freteVal > 0 && (
                    <div className={s.totaisLinha}>
                      <span>Frete:</span><span>{fmtMoeda(freteVal)}</span>
                    </div>
                  )}
                  <hr style={{ margin: '4px 0', borderColor: '#ccc' }} />
                  <div className={s.totaisLinha}>
                    <strong>Total Geral:</strong><strong>{fmtMoeda(totalGeral)}</strong>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ─── ABA FINANCEIRO ─── */}
          {aba === 'financeiro' && (
            <div className={s.dualGrid}>
              <fieldset className={s.fieldset}>
                <legend className={s.legend}>Pagamento</legend>
                <div className={s.campo}>
                  <span className={s.campoLabel}>Forma de Pgto.</span>
                  <select className={s.selectErp} style={{ flex: 1 }}
                    value={form.forma_pagamento} disabled={!editando}
                    onChange={e => set('forma_pagamento', e.target.value)}>
                    <option value="">— Selecione —</option>
                    {FORMA_PGT_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                  {aprovadoSemPgto && editando && (
                    <span style={{ color: '#c00', fontSize: 11, marginLeft: 4 }}>⚠ obrigatória</span>
                  )}
                </div>
                <div className={s.campo}>
                  <span className={s.campoLabel}>Cond. Pagamento</span>
                  <input className={s.inputErp} style={{ flex: 1 }}
                    value={form.condicao_pagamento} readOnly={!editando}
                    placeholder="Ex: 30/60/90 dias"
                    onChange={e => set('condicao_pagamento', e.target.value)} />
                </div>
              </fieldset>

              <fieldset className={s.fieldset}>
                <legend className={s.legend}>Valores</legend>
                <div className={s.campo}>
                  <span className={s.campoLabel}>Subtotal Itens</span>
                  <input className={s.inputErp} style={{ width: 120, textAlign: 'right' }}
                    value={fmtMoeda(subTotal)} readOnly />
                </div>
                <div className={s.campo}>
                  <span className={s.campoLabel}>Desconto R$</span>
                  <input className={s.inputErp} style={{ width: 120, textAlign: 'right' }}
                    type="number" min="0" step="0.01"
                    value={form.desconto} readOnly={!editando} placeholder="0,00"
                    onChange={e => set('desconto', e.target.value)} />
                </div>
                <div className={s.campo}>
                  <span className={s.campoLabel}>Frete R$</span>
                  <input className={s.inputErp} style={{ width: 120, textAlign: 'right' }}
                    type="number" min="0" step="0.01"
                    value={form.frete} readOnly={!editando} placeholder="0,00"
                    onChange={e => set('frete', e.target.value)} />
                </div>
                <div className={s.campo} style={{ borderTop: '1px solid #ddd', paddingTop: 4, marginTop: 2 }}>
                  <span className={s.campoLabel} style={{ fontWeight: 'bold' }}>Total Geral</span>
                  <input className={s.inputErp}
                    style={{ width: 120, textAlign: 'right', fontWeight: 'bold', fontSize: 14 }}
                    value={fmtMoeda(totalGeral)} readOnly />
                </div>
              </fieldset>
            </div>
          )}

          {/* ─── ABA TRANSPORTE ─── */}
          {aba === 'transporte' && (
            <div style={{ maxWidth: 560 }}>
              <fieldset className={s.fieldset}>
                <legend className={s.legend}>Frete / Entrega</legend>
                <div className={s.campo}>
                  <span className={s.campoLabel}>Tipo de Frete</span>
                  <select className={s.selectErp} style={{ flex: 1 }}
                    value={form.tipo_frete} disabled={!editando}
                    onChange={e => set('tipo_frete', e.target.value)}>
                    {TIPO_FRETE_OPTS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                </div>
                <div className={s.campo}>
                  <span className={s.campoLabel}>Transportadora</span>
                  <input className={s.inputErp} style={{ flex: 1 }}
                    value={form.transportadora} readOnly={!editando}
                    placeholder="Nome da transportadora"
                    onChange={e => set('transportadora', e.target.value)} />
                </div>
                <div className={s.campo}>
                  <span className={s.campoLabel}>Prazo (dias)</span>
                  <input className={s.inputErp} style={{ width: 80, textAlign: 'right' }}
                    type="number" min="0"
                    value={form.prazo_entrega} readOnly={!editando} placeholder="0"
                    onChange={e => set('prazo_entrega', e.target.value)} />
                </div>
              </fieldset>
              {form.cliente_nome && (
                <fieldset className={s.fieldset}>
                  <legend className={s.legend}>Cliente</legend>
                  <div style={{ fontSize: 12, color: '#333', lineHeight: 1.7 }}>
                    <strong>{form.cliente_nome}</strong>
                    {form.cliente_cpf_cnpj && <><br />CPF/CNPJ: {form.cliente_cpf_cnpj}</>}
                  </div>
                </fieldset>
              )}
            </div>
          )}

          {/* ─── ABA PROPOSTA (Parâmetros → Vendas → Proposta Comercial) ─── */}
          {aba === 'proposta' && orgData?.proposta_comercial_ativa && (() => {
            const pr = form.proposta || {};
            const cfg = orgData.proposta_config || {};
            const setPr = (k, v) => setForm(f => ({ ...f, proposta: { ...(f.proposta || {}), [k]: v } }));
            const campo = (k, rotulo, largura, extra = {}) => (
              <label key={k} style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, color: '#444', width: largura }}>
                {rotulo}
                <input className={s.inputErp} value={pr[k] || ''} readOnly={!editando}
                  onChange={e => setPr(k, e.target.value)} {...extra} />
              </label>
            );
            return (
              <div style={{ maxWidth: 920 }}>
                <fieldset className={s.fieldset}>
                  <legend className={s.legend}>Equipamento</legend>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {campo('equip_tipo', 'Equipamento (ex.: Soft Starter, Motor)', 260)}
                    {CAMPOS_EQUIP.map(([k, rotulo, largura]) => campo(k, rotulo, largura))}
                  </div>
                </fieldset>
                <fieldset className={s.fieldset}>
                  <legend className={s.legend}>Nota de entrada do equipamento</legend>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {campo('nf_entrada_numero', 'Número', 120)}
                    {campo('nf_entrada_serie', 'Série', 70)}
                    {campo('nf_entrada_data', 'Data', 140, { type: 'date' })}
                  </div>
                </fieldset>
                <fieldset className={s.fieldset}>
                  <legend className={s.legend}>Proposta</legend>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {campo('aos_cuidados', 'A/C (aos cuidados de)', 240)}
                    {campo('garantia_meses', 'Garantia (meses)', 110, { placeholder: cfg.garantia_meses || '', inputMode: 'numeric' })}
                    {campo('impostos', 'Impostos', 120, { placeholder: cfg.impostos || '' })}
                    {campo('local_servico', 'Local de realização dos serviços', 260, { placeholder: cfg.local_servico || '' })}
                    {campo('identificacao', 'Identificação (linha livre na proposta)', 520, { placeholder: 'Ex.: SOFT WEG SSW070045T5SZ DATA:21P' })}
                  </div>
                  <div style={{ fontSize: 11, color: '#666', marginTop: 6 }}>
                    Em branco, garantia/impostos/local usam o padrão de Parâmetros → Vendas. Prazo de entrega,
                    pagamento, frete e validade vêm das abas Transporte e Financeiro e do cabeçalho.
                  </div>
                </fieldset>
              </div>
            );
          })()}

          {/* ─── ABA OBS ─── */}
          {aba === 'obs' && (
            <textarea className={s.textareaObs}
              readOnly={!editando} value={form.observacoes}
              placeholder={editando ? 'Observações do pedido...' : ''}
              onChange={e => set('observacoes', e.target.value)} />
          )}
        </div>

        {/* ── Status bar ── */}
        <div className={s.statusBar}>{statusText()}</div>

        {/* ── Rodapé ── */}
        <div className={s.footer}>
          <div className={s.footerLeft}>
            <button className={`${s.btnErp} ${s.btnErpNav}`}
              onClick={navAnterior} disabled={editando || posicao === 0}>&lt;</button>
            <span className={s.footerPos}>
              {lista.length ? `${posicao + 1} / ${lista.length}` : '0 / 0'}
            </span>
            <button className={`${s.btnErp} ${s.btnErpNav}`}
              onClick={navProximo} disabled={editando || posicao >= lista.length - 1}>&gt;</button>
            <span className={s.f3Hint} onClick={() => { setF3Modo('consulta'); abrirF3(); }} title="Consulta de produtos (F3)">
              F3 – Consulta Produtos
            </span>
          </div>
          <div className={s.footerRight}>
            <button className={s.btnErp} onClick={handleNovo} disabled={editando} title="Novo pedido/orçamento (F4)">Novo (F4)</button>
            <button className={s.btnErp} onClick={() => setModalBusca(true)} disabled={editando}>Buscar</button>
            <button className={s.btnErp} onClick={handleAlterar}
              disabled={editando || !form.id || form.status === 'cancelado' || form.status === 'importado'}>
              Alterar
            </button>
            <button className={s.btnErp} onClick={handleCancelar} disabled={!editando}>Cancelar</button>
            <button className={`${s.btnErp} ${s.btnErpPerigo}`} onClick={handleExcluir}
              disabled={editando || !form.id || form.status === 'cancelado' || form.status === 'importado'}>
              Excluir
            </button>
            <button className={`${s.btnErp} ${s.btnErpPrimario}`}
              onClick={handleSalvar} disabled={!editando || salvando}
              title="Salvar (F9)">
              {salvando ? 'Salvando...' : 'Salvar (F9)'}
            </button>
            {form.id && (
              <>
                {['aberto', 'aprovado'].includes(form.status) && orgData?.finalizar_venda_pedido && (
                  <button className={`${s.btnErp} ${s.btnErpPrimario}`}
                    onClick={finalizarVendaPedido}
                    disabled={editando || finalizando || !form.itens.length}
                    title={form.status === 'aberto'
                      ? 'Aprova o pedido automaticamente e converte em venda finalizada (F9)'
                      : 'Converte este pedido em venda finalizada (F9)'}>
                    {finalizando ? 'Finalizando…' : '💰 Finalizar Venda (F9)'}
                  </button>
                )}
                {form.status === 'importado' && (
                  <button className={`${s.btnErp} ${s.btnErpPerigo}`}
                    onClick={cancelarVendaDoPedido} disabled={editando || cancelandoVenda}
                    title="Cancela a venda faturada deste pedido: devolve o estoque, desfaz o financeiro e cancela o documento fiscal na SEFAZ (senha de administrador)">
                    {cancelandoVenda ? 'Cancelando…' : '🚫 Cancelar Venda'}
                  </button>
                )}
                <button className={s.btnErp} onClick={imprimirTermica} disabled={editando}
                  title="Imprimir cupom 80mm">
                  Térmica
                </button>
                <button className={s.btnErp} onClick={imprimirA4} disabled={editando}
                  title="Imprimir folha A4">
                  A4
                </button>
                {orgData?.proposta_comercial_ativa && (
                  <button className={s.btnErp} onClick={imprimirProposta} disabled={editando}
                    title="Imprimir a Proposta Comercial deste orçamento (dados da aba Proposta)">
                    Proposta
                  </button>
                )}
                <button className={s.btnErp} onClick={abrirLogs} disabled={editando}
                  title="Histórico de eventos deste pedido">
                  Histórico
                </button>
              </>
            )}
            <div className={s.footerSep} />
            <button className={s.btnErp} onClick={() => window.history.back()}>Sair</button>
          </div>
        </div>
      </div>

      {/* ═══ Modais ═══ */}

      {/* Pagamento da finalização — mesma experiência do F9 do PDV */}
      {/* Venda no VENDA CONSUMIDOR → escolher cliente p/ NF-e (422 exige_cliente) */}
      {modalSelecionarCli && (
        <ModalSelecionarCliente
          onSelect={async (c) => {
            try {
              await api.put(`/vendas/${modalSelecionarCli.vendaId}/cliente`, { cliente_id: c.id });
              setForm(f => ({ ...f, cliente_tipo_pessoa: c.tipo_pessoa || 'F' }));
              const r = modalSelecionarCli.retry;
              setModalSelecionarCli(null);
              r();
            } catch (err) {
              alert(err.response?.data?.erro || 'Erro ao definir o cliente da venda.');
            }
          }}
          onClose={() => setModalSelecionarCli(null)}
        />
      )}

      {/* Correção fiscal de produtos (mesmo modal do PDV) */}
      <ModalCorrecaoFiscal
        modalCorrecao={modalCorrecao}
        setModalCorrecao={setModalCorrecao}
        resultadoDocMov={correcaoDados}
        correcoes={correcoes}
        setCorrecoes={setCorrecoes}
        salvandoCorrecao={salvandoCorrecao}
        salvarEReemitir={salvarEReemitir}
        correcaoTipo={correcaoTipo}
      />

      {/* Cadastro do cliente incompleto p/ NF-e (422 cliente_incompleto) */}
      {modalCompletarCli && (
        <ModalCompletarCliente
          clienteId={modalCompletarCli.clienteId}
          faltando={modalCompletarCli.faltando}
          onSalvo={() => { const r = modalCompletarCli.retry; setModalCompletarCli(null); r(); }}
          onClose={() => setModalCompletarCli(null)}
        />
      )}

      <ModalPagamentoVenda
        aberto={modalPagamento}
        onFechar={() => setModalPagamento(false)}
        totalVenda={totalGeral}
        cliente={form.cliente_id ? { id: form.cliente_id, nome: form.cliente_nome } : null}
        tipoMovFiscal={tipoMovSelecionado}
        onConfirmar={confirmarPagamentoPedido}
        onClienteChange={c => {
          if (c) aplicarCliente(c);
          else if (clientePadrao) aplicarCliente(clientePadrao); // "limpar" volta ao 1-VENDA CONSUMIDOR
          else setForm(f => ({ ...f, cliente_id: null, cliente_codigo: '', cliente_nome: '', cliente_cpf_cnpj: '', cliente_tipo_pessoa: 'F' }));
        }}
      />

      {/* Documento fiscal da venda finalizada a partir do pedido */}
      {modalDocFiscal && (
        <div className={s.overlay}>
          <div className={s.modal} style={{ maxWidth: 480 }}>
            <div className={s.modalTitle}>Venda #{modalDocFiscal.vendaId} finalizada — Documento Fiscal</div>
            <div className={s.modalBody}>
              {modalDocFiscal.boletos?.length > 0 && (
                <div style={{ background: '#eff6ff', border: '1px solid #3b82f6', borderRadius: 6,
                  padding: '8px 12px', marginBottom: 10, fontSize: 12, color: '#1e40af',
                  display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span>🏦 {modalDocFiscal.boletos.length} boleto(s) gerado(s)</span>
                  {modalDocFiscal.boletos.map(b => (
                    <button key={b.id} className={s.btnErp} style={{ height: 24, fontSize: 12 }}
                      onClick={() => abrirBoleto(b)}>
                      🖨️ Boleto #{b.id}
                    </button>
                  ))}
                </div>
              )}
              {modalDocFiscal.aviso_boleto && (
                <div style={{ background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 6,
                  padding: '8px 12px', marginBottom: 10, fontSize: 12, color: '#92400e' }}>
                  ⚠ {modalDocFiscal.aviso_boleto}
                </div>
              )}
              {itensServicoVenda?.length > 0 && (
                <div style={{ background: '#f5f3ff', border: '1px solid #a78bfa', borderRadius: 6,
                  padding: '8px 12px', marginBottom: 10, fontSize: 12, color: '#4c1d95' }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>🧾 Serviços desta venda (NFS-e)</div>
                  {itensServicoVenda.map(item => {
                    const resultado = nfseResultados?.[item.id];
                    return (
                      <div key={item.id} style={{ marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                          <span>{item.descricao} — R$ {fmtMoeda(parseFloat(item.subtotal))}</span>
                          <button className={s.btnErp} style={{ height: 24, fontSize: 12 }}
                            disabled={nfseEmitindo === item.id}
                            onClick={() => emitirNfseItem(item)}>
                            {nfseEmitindo === item.id ? 'Gerando…' : '🧾 Gerar DANFE'}
                          </button>
                        </div>
                        {resultado && (resultado.erro || resultado.motivo) && (
                          <div style={{ marginTop: 4, fontSize: 11, color: resultado.sucesso ? '#57534e' : '#b91c1c' }}>
                            {resultado.erro || resultado.motivo}
                            {resultado.status ? ` (status ${resultado.status})` : ''}
                          </div>
                        )}
                        {resultado?.sucesso && (
                          <div style={{ marginTop: 4, fontSize: 11, color: '#15803d' }}>
                            ✔ NFS-e nº {resultado.numero_nfse} emitida com sucesso.
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {emitindoDoc && <p style={{ textAlign: 'center', padding: 14 }}>Transmitindo para SEFAZ…</p>}

              {!emitindoDoc && resultadoDoc && (
                <div style={{ padding: 10, borderRadius: 6, marginBottom: 10,
                  background: resultadoDoc.sucesso ? '#ecfdf5' : '#fef2f2',
                  border: `1px solid ${resultadoDoc.sucesso ? '#10b981' : '#ef4444'}` }}>
                  <strong>{resultadoDoc.sucesso ? '✔ Autorizado' : '✘ Rejeitado/Erro'}</strong>
                  <div style={{ fontSize: 12, marginTop: 4 }}>{resultadoDoc.motivo}</div>
                  {resultadoDoc.chave_acesso && (
                    <div style={{ fontSize: 10, wordBreak: 'break-all', marginTop: 4 }}>
                      Chave: {resultadoDoc.chave_acesso}
                    </div>
                  )}
                  {resultadoDoc.protocolo && (
                    <div style={{ fontSize: 11 }}>Protocolo: {resultadoDoc.protocolo}</div>
                  )}
                  {resultadoDoc.aviso && (
                    <div style={{ fontSize: 12, marginTop: 6, fontWeight: 600 }}>{resultadoDoc.aviso}</div>
                  )}
                  {/* NF-e falhou (SEFAZ fora do ar?) → oferece contingência EPEC */}
                  {resultadoDoc.tipo === 'nfe' && !resultadoDoc.sucesso && (
                    <button className={s.btnErp} style={{ marginTop: 8 }}
                      title="SEFAZ fora do ar: registra o EPEC no Ambiente Nacional e libera a circulação; a NF-e é transmitida depois pela Central de Notas (prazo 168h)"
                      onClick={() => emitirEpecVenda()}>
                      ⚡ Emitir em contingência (EPEC)
                    </button>
                  )}
                </div>
              )}

              {!emitindoDoc && !resultadoDoc?.sucesso && (
                <>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10, fontSize: 12 }}>
                    <span>Largura do cupom:</span>
                    {['58mm', '80mm'].map(l => (
                      <label key={l} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input type="radio" name="larguraVenda"
                          checked={larguraCupomVenda === l}
                          onChange={() => setLarguraCupomVenda(l)} />
                        {l}
                      </label>
                    ))}
                  </div>
                  {form.cliente_tipo_pessoa === 'J' && (
                    <p style={{ fontSize: 11.5, color: '#92400e', background: '#fffbeb',
                      border: '1px solid #f59e0b', borderRadius: 6, padding: '4px 10px', marginBottom: 8 }}>
                      Cliente pessoa jurídica — disponível apenas NF-e (e NFS-e para itens de serviço).
                    </p>
                  )}
                  <div style={{ display: 'grid', gap: 6 }}>
                    {form.cliente_tipo_pessoa !== 'J' && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className={s.btnErp} style={{ flex: 1 }} onClick={() => emitirDocVenda('nfce')}>
                          <b>1</b> — Emitir NFC-e
                        </button>
                        <button className={s.btnErp} title="Gera uma prévia sem transmitir à SEFAZ"
                          disabled={gerandoPrevia === 'nfce'} onClick={() => previaDocVenda('nfce')}>
                          {gerandoPrevia === 'nfce' ? 'Gerando…' : '👁 Prévia'}
                        </button>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className={s.btnErp} style={{ flex: 1 }} onClick={() => emitirDocVenda('nfe')}>
                        <b>2</b> — Emitir NF-e
                      </button>
                      <button className={s.btnErp} title="Gera uma prévia sem transmitir à SEFAZ"
                        disabled={gerandoPrevia === 'nfe'} onClick={() => previaDocVenda('nfe')}>
                        {gerandoPrevia === 'nfe' ? 'Gerando…' : '👁 Prévia'}
                      </button>
                    </div>
                    <button className={s.btnErp} onClick={() => imprimirVendaFinalizada('termica')}>
                      <b>3</b> — Imprimir cupom não fiscal ({larguraCupomVenda})
                    </button>
                    <button className={s.btnErp} onClick={() => imprimirVendaFinalizada('a4')}>
                      <b>4</b> — Imprimir A4
                    </button>
                  </div>
                </>
              )}
            </div>
            <div className={s.modalFooter}>
              {resultadoDoc?.sucesso && resultadoDoc.tipo === 'nfce' && (
                <button className={s.btnErp} onClick={async () => {
                  try {
                    const { data: v } = await api.get(`/vendas/${modalDocFiscal.vendaId}`);
                    gerarDanfeNfceVenda(v, resultadoDoc, larguraCupomVenda, orgData || {});
                  } catch { alert('Erro ao reimprimir DANFE.'); }
                }}>Reimprimir DANFE</button>
              )}
              {resultadoDoc?.sucesso && resultadoDoc.tipo === 'nfe' && resultadoDoc.documento_id && (
                <button className={s.btnErp}
                  onClick={() => abrirDanfeNfePdf(`/fiscal/nfe/${resultadoDoc.documento_id}/danfe`)}>
                  Reimprimir DANFE
                </button>
              )}
              <button className={`${s.btnErp} ${s.btnErpPrimario}`}
                onClick={() => { setModalDocFiscal(null); setResultadoDoc(null); setItensServicoVenda([]); }}>
                {resultadoDoc?.sucesso ? 'Concluir' : 'Fechar'}
              </button>
            </div>
          </div>
        </div>
      )}
      {modalBusca && (
        <ModalBuscaPedido
          onSelect={async p => {
            const i = lista.findIndex(l => l.id === p.id);
            await carregarPedido(p.id, lista, i >= 0 ? i : posicao);
            setModalBusca(false);
          }}
          onClose={() => setModalBusca(false)}
        />
      )}

      {modalCliente && (
        <ModalBuscaCliente onSelect={onClienteSelect} onClose={() => setModalCliente(false)} />
      )}

      {modalF3 && (
        <ModalBuscaProdutos
          modalF3={modalF3} fecharF3={fecharF3}
          filtrosF3={filtrosF3} setFiltro={setFiltro} setFiltrosF3={setFiltrosF3}
          produtosF3={produtosF3} setProdutosF3={setProdutosF3}
          buscaRapidaF3={buscaRapidaF3} setBuscaRapidaF3={setBuscaRapidaF3}
          handleBuscaRapidaF3={handleBuscaRapidaF3} buscandoF3={buscandoF3}
          buscarF3={buscarF3} idxF3={idxF3} setIdxF3={setIdxF3}
          confirmarF3={confirmarF3} tipoMovFiscal={tipoMovRef.current}
          buscaRapidaRef={buscaRapidaRef} consultaOnly={f3Modo === 'consulta'}
          tabela={tabelaPrecoCliente}
        />
      )}

      {modalSetor && (
        <ModalSetor
          produtoId={modalSetor.produtoId}
          produtoNome={modalSetor.produtoNome}
          onConfirm={confirmarSetor}
          onClose={fecharModalSetor}
        />
      )}

      {/* ════ MODAL CONFIRMAÇÃO TABELA DE PREÇO ════ */}
      {modalTabelaPreco && (
        <div className={s.confirmOverlay}>
          <div className={s.confirmBox}>
            <div className={s.confirmTitulo}>Tabela de Preço Detectada</div>
            <div className={s.confirmCorpo}>
              <p>
                O cliente <strong>{modalTabelaPreco.clientePendente.nome}</strong> possui
                a tabela de preço <strong>"{modalTabelaPreco.tabela.descricao}"</strong>.
              </p>
              <p>Deseja atualizar os preços dos itens conforme esta tabela?</p>
            </div>
            <div className={s.confirmBtns}>
              <button className={`${s.btnErp} ${s.btnSim}`}
                onClick={() => confirmarTabelaPreco('sim')}>
                Sim — Atualizar Preços
              </button>
              <button className={`${s.btnErp} ${s.btnNao}`}
                onClick={() => confirmarTabelaPreco('nao')}>
                Não — Manter Preços Atuais
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════ MODAL HISTÓRICO / LOGS ════ */}
      {modalLogs && (
        <div className={s.overlay} onMouseDown={e => e.target === e.currentTarget && setModalLogs(false)}>
          <div className={s.modalLogs} onClick={e => e.stopPropagation()}>
            <div className={s.modalTitle} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Histórico — Pedido #{form.id}</span>
              <button style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16 }}
                onClick={() => setModalLogs(false)}>✕</button>
            </div>
            <div className={s.logsCorpo}>
              {carregandoLogs && <div className={s.logsVazio}>Carregando...</div>}
              {!carregandoLogs && logs.length === 0 && (
                <div className={s.logsVazio}>Nenhum evento registrado para este pedido.</div>
              )}
              {!carregandoLogs && logs.length > 0 && (
                <table className={s.logsTabela}>
                  <thead>
                    <tr>
                      <th>Data/Hora</th>
                      <th>Tipo</th>
                      <th>Descrição</th>
                      <th>Resposta</th>
                      <th>Tabela</th>
                      <th>Usuário</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log => (
                      <tr key={log.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {new Date(log.criado_em).toLocaleString('pt-BR')}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {log.tipo === 'atualizacao_preco_tabela' ? 'Tabela de Preço' : log.tipo}
                        </td>
                        <td>{log.descricao}</td>
                        <td style={{ textAlign: 'center' }}>
                          {log.resposta && (
                            <span className={log.resposta === 'sim' ? s.logRespostaSim : s.logRespostaNao}>
                              {log.resposta.toUpperCase()}
                            </span>
                          )}
                        </td>
                        <td>{log.tabela_nome || '—'}</td>
                        <td>{log.usuario_nome || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className={s.logsRodape}>
              <button className={s.btnErp} onClick={() => setModalLogs(false)}>Fechar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
